import { createBidsApi, BidsApi } from './domains/bidsApi';
import { createPersonalTimesheetApi } from './domains/personalTimesheetApi';
import axios, { AxiosInstance } from 'axios';
import type { TicketSheetInput, TicketSignatureInput, TicketDocumentsResponse } from './ticketDocumentTypes';
import { getApiBaseUrl, resolveApiFileUrl } from '../utils/apiFileUrl';
import toast from 'react-hot-toast';
import {
  AccountSecurityOverview,
  PersonNameMatch,
  RentedWorkerDirectory,
  TimesheetOverlap,
  MaintenanceWindow,
  ProjectRentalCrew,
  ProjectRentedIdentityDirectory,
  ProjectWorkCrew,
  WorkCrewHistoricalReclassificationPreview,
} from '../types';
import { createDashboardApi, DashboardApi } from './domains/dashboardApi';
import { createForecastApi, ForecastApi } from './domains/forecastApi';
import { createHolidayApi, HolidayApi } from './domains/holidayApi';
import { createPeopleApi, PeopleApi } from './domains/peopleApi';
import { createPtoScheduleApi, PtoScheduleApi } from './domains/ptoScheduleApi';
import { createProjectOverviewSnapshotApi, ProjectOverviewSnapshotApi } from './domains/projectOverviewSnapshotApi';
import { createProjectCoordinationApi, ProjectCoordinationApi } from './domains/projectCoordinationApi';
import { createSecurityApi, SecurityApi } from './domains/securityApi';
import { createSettingsApi, SettingsApi } from './domains/settingsApi';
import { createAuthzApi, AuthzApi } from './domains/authzApi';
import { createTimesheetApi, TimesheetApi } from './domains/timesheetApi';
import { createWarehouseApi, WarehouseApi } from './domains/warehouseApi';
import { getPermissionPreviewToken, isPermissionPreview } from './permissionPreviewSession';

const API_BASE_URL = getApiBaseUrl();
// Accept every supported cookie name so local access behaves the same through
// localhost, a LAN address, or the development server's same-origin proxy.
const COOKIE_NAMES = {
  csrf: ['demo_csrf_token', 'csrf_token', '__Host-csrf_token'],
};

class ApiClient {
  async startProjectUnfiledDocumentUpload(projectId: number, input: Omit<import('../types').ProjectDocumentUploadInput, 'folder_id'>): Promise<import('../types').ProjectDocumentUploadSession> {
    const categories = await this.getProjectDocumentCategories(projectId);
    const category = categories.find((item) => item.stable_key === 'miscellaneous' && item.is_enabled);
    if (!category) throw new Error('The root document category is not configured');
    return (await this.client.post(`/projects/${projectId}/documents/upload-session`, { ...input, category_id: category.id })).data;
  }

  async projectDocumentExplorerOperation(projectId: number, input: import('../types').ProjectDocumentExplorerOperation): Promise<{ files: import('../types').ProjectDocumentDownloadItem[] }> {
    return (await this.client.post(`/projects/${projectId}/document-explorer/${input.action === 'download' ? 'download' : 'operations'}`, input)).data;
  }
  async createProjectDocumentChildFolder(projectId: number, name: string, parentId: number): Promise<import('../types').ProjectDocumentFolder> {
    return (await this.client.post(`/projects/${projectId}/document-folders`, { name, parent_id: parentId })).data.folder;
  }
  async downloadProjectDocumentVersion(projectId: number, documentId: number, versionId: number): Promise<Blob> {
    return (await this.client.get(`/projects/${projectId}/documents/${documentId}/versions/${versionId}/content`, { responseType: 'blob' })).data;
  }

  async getTicketDocuments(projectId: number, ticketId: number): Promise<TicketDocumentsResponse> {
    return (await this.client.get(`/projects/${projectId}/ticket-work-orders/${ticketId}/documents`)).data;
  }
  async createTicketDocument(projectId: number, ticketId: number, input: TicketSheetInput) {
    return (await this.client.post(`/projects/${projectId}/ticket-work-orders/${ticketId}/documents`, input)).data;
  }
  async downloadTicketDocument(projectId: number, ticketId: number, id: number): Promise<Blob> {
    return (await this.client.get(`/projects/${projectId}/ticket-work-orders/${ticketId}/documents/${id}/pdf`, { responseType: 'blob' })).data;
  }
  async requestTicketSignatureLink(projectId: number, ticketId: number, id: number, email: string, sendEmail = true, verification: 'none' | 'email_code' = 'email_code'): Promise<{ path: string; expires_at: string; delivery: 'sent' | 'not_configured' | 'failed' | 'skipped' }> {
    return (await this.client.post(`/projects/${projectId}/ticket-work-orders/${ticketId}/documents/${id}/link`, { ...(email ? { email } : {}), send_email: sendEmail, verification })).data;
  }
  async revokeTicketDocument(projectId: number, ticketId: number, id: number) {
    return (await this.client.post(`/projects/${projectId}/ticket-work-orders/${ticketId}/documents/${id}/revoke`)).data;
  }
  async requestTicketSignatureAuthorization(projectId: number, ticketId: number, id: number) {
    return (await this.client.post(`/projects/${projectId}/ticket-work-orders/${ticketId}/documents/${id}/authorization-request`)).data;
  }
  async authorizeTicketSignature(projectId: number, ticketId: number, id: number) {
    return (await this.client.post(`/projects/${projectId}/ticket-work-orders/${ticketId}/documents/${id}/authorize`)).data;
  }
  async signTicketDocument(projectId: number, ticketId: number, id: number, input: TicketSignatureInput) {
    return (await this.client.post(`/projects/${projectId}/ticket-work-orders/${ticketId}/documents/${id}/sign`, input)).data;
  }
  getBids(...args: Parameters<BidsApi['getBids']>) { return this.bidsApi.getBids(...args); }
  getBidWorkspace() { return this.bidsApi.getBidWorkspace(); }
  getBid(...args: Parameters<BidsApi['getBid']>) { return this.bidsApi.getBid(...args); }
  saveBid(...args: Parameters<BidsApi['saveBid']>) { return this.bidsApi.saveBid(...args); }
  archiveBid(...args: Parameters<BidsApi['archiveBid']>) { return this.bidsApi.archiveBid(...args); }
  getBidProjectChoices(...args: Parameters<BidsApi['getBidProjectChoices']>) { return this.bidsApi.getBidProjectChoices(...args); }
  winBid(...args: Parameters<BidsApi['winBid']>) { return this.bidsApi.winBid(...args); }
  startBidUpload(...args: Parameters<BidsApi['startBidUpload']>) { return this.bidsApi.startBidUpload(...args); }
  uploadBidChunk(...args: Parameters<BidsApi['uploadBidChunk']>) { return this.bidsApi.uploadBidChunk(...args); }
  downloadBidFile(...args: Parameters<BidsApi['downloadBidFile']>) { return this.bidsApi.downloadBidFile(...args); }
  transferBidFile(...args: Parameters<BidsApi['transferBidFile']>) { return this.bidsApi.transferBidFile(...args); }
  private client: AxiosInstance;
  private bidsApi: BidsApi;
  private dashboardApi: DashboardApi;
  private forecastApi: ForecastApi;
  private holidayApi: HolidayApi;
  private peopleApi: PeopleApi;
  private ptoScheduleApi: PtoScheduleApi;
  private projectOverviewSnapshotApi: ProjectOverviewSnapshotApi;
  private projectCoordinationApi: ProjectCoordinationApi;
  private refreshPromise: Promise<any> | null = null;
  private securityApi: SecurityApi;
  private settingsApi: SettingsApi;
  private authzApi: AuthzApi;
  private timesheetApi: TimesheetApi;
  private warehouseApi: WarehouseApi;

  constructor() {
    this.client = axios.create({
      baseURL: API_BASE_URL,
      withCredentials: true,
      timeout: 15000,
      headers: {
        'Content-Type': 'application/json',
      },
    });
    this.bidsApi = createBidsApi(this.client);
    this.dashboardApi = createDashboardApi(this.client);
    this.forecastApi = createForecastApi(this.client);
    this.holidayApi = createHolidayApi(this.client);
    this.peopleApi = createPeopleApi(this.client);
    this.ptoScheduleApi = createPtoScheduleApi(this.client);
    this.projectOverviewSnapshotApi = createProjectOverviewSnapshotApi(this.client);
    this.projectCoordinationApi = createProjectCoordinationApi(this.client);
    this.securityApi = createSecurityApi(this.client);
    this.settingsApi = createSettingsApi(this.client);
    this.authzApi = createAuthzApi(this.client);
    this.timesheetApi = createTimesheetApi(this.client);
    this.warehouseApi = createWarehouseApi(this.client);

    this.client.interceptors.request.use((config) => {
      const preview = getPermissionPreviewToken();
      if (preview) {
        config.headers['X-Forxa-Permission-Preview'] = preview;
        if (!['get', 'head'].includes(String(config.method || 'get').toLowerCase())) {
          toast.error('Vista previa de solo lectura: no se guardan cambios.', { id: 'permission-preview-read-only' });
          return Promise.reject(Object.assign(new Error('Permission preview is read-only'), { permissionPreviewReadOnly: true }));
        }
      }
      if (config.method && ['post', 'patch', 'put', 'delete'].includes(config.method.toLowerCase())) {
        const csrfToken = COOKIE_NAMES.csrf
          .map((name) => this.getCookie(name))
          .find(Boolean);
        if (csrfToken) {
          config.headers['X-CSRF-Token'] = csrfToken;
        }
      }
      return config;
    });

    this.client.interceptors.response.use(
      (response) => response,
      async (error) => {
        // Virtual Drawings rows and route changes deliberately cancel reads.
        // Cancellation is not an outage and must not retry an aborted signal.
        if (axios.isCancel(error)) return Promise.reject(error);
        if (error.permissionPreviewReadOnly) return Promise.reject(error);
        const originalRequest = error.config;
        const suppressAuthRedirect = Boolean(originalRequest?.suppressAuthRedirect);
        const suppressAuthRefresh = Boolean(originalRequest?.suppressAuthRefresh);
        const method = String(originalRequest?.method || 'get').toLowerCase();
        const isReadRequest = ['get', 'head'].includes(method);
        const isNetworkFailure = !error.response;
        const shouldTryRefresh =
          !originalRequest?._retry &&
          !suppressAuthRefresh &&
          originalRequest?.url !== '/auth/refresh' &&
          (
            error.response?.data?.error === 'Invalid or expired token'
            || error.response?.data?.error === 'Access token required'
          );

        if (shouldTryRefresh && !isPermissionPreview()) {
          originalRequest._retry = true;

          try {
            if (!this.refreshPromise) {
              this.refreshPromise = this.refreshSession().finally(() => {
                this.refreshPromise = null;
              });
            }

            await this.refreshPromise;
            return this.client(originalRequest);
          } catch {
            this.clearAccessToken();
            if (!suppressAuthRedirect && window.location.pathname !== '/login') {
              window.location.href = '/login?reason=session-expired';
            }
          }
        }

        if (!isPermissionPreview() && error.response?.status === 403 && error.response?.data?.error === 'Invalid or expired token') {
          this.clearAccessToken();
          if (!suppressAuthRedirect && window.location.pathname !== '/login') {
            window.location.href = '/login?reason=session-expired';
          }
        }

        // Mobile browsers frequently pause network activity while the app is in
        // the background. Retry one safe read after wake-up, but never eject the
        // user from the module merely because a request timed out.
        if (isNetworkFailure && isReadRequest && originalRequest && !originalRequest._networkRetry) {
          originalRequest._networkRetry = true;
          await new Promise((resolve) => window.setTimeout(resolve, 750));
          return this.client(originalRequest);
        }

        if (isNetworkFailure && isReadRequest && !suppressAuthRedirect) {
          toast.error('Connection is slow or unavailable. Please try again.', {
            id: 'api-read-network-error',
            duration: 5000,
          });
        }

        // For non-GET network failures (no response at all) show a global toast
        // so the user always knows their mutation didn't reach the server.
        if (isNetworkFailure && !suppressAuthRedirect && !isReadRequest) {
          toast.error('Network error — check your connection. Your changes may not have been saved.', {
            id: 'api-network-error',
            duration: 6000,
          });
        }

        const responseMessage = String(error.response?.data?.error || '');
        const isPermissionError = error.response?.status === 403 && (
          responseMessage === 'Insufficient permissions' ||
          responseMessage === 'Module access denied' ||
          responseMessage === 'Owner admin only' ||
          responseMessage === 'Operator access required' ||
          responseMessage === 'Admin only' ||
          responseMessage === 'Not authorized' ||
          responseMessage === 'Not assigned to this project'
        );
        if (isPermissionError && !suppressAuthRedirect && method === 'get' && !window.location.pathname.startsWith('/error/')) {
          window.location.href = '/error/forbidden';
        }

        // A 400 response normally describes one invalid request or stale filter,
        // not a broken application. Let the owning screen show its inline error
        // instead of replacing the entire SPA with /error/data.
        return Promise.reject(error);
      },
    );
  }

  setAccessToken(_token: string | null) {}

  clearAccessToken() {}

  private getCookie(name: string) {
    if (typeof document === 'undefined') return null;
    const match = document.cookie
      .split('; ')
      .find((row) => row.startsWith(`${name}=`));
    return match ? decodeURIComponent(match.split('=').slice(1).join('=')) : null;
  }

  // Auth endpoints
  async createPermissionPreview(data: { user_id: number; module_permissions: unknown; sub_module_permissions: unknown; access_scope: string; template_key: string | null }) {
    return (await this.client.post('/permission-preview', data)).data as { token: string; expires_in_seconds: number };
  }
  async login(email: string, password: string) {
    const response = await this.client.post('/auth/login', { email, password });
    return response.data;
  }

  async refreshSession() {
    try {
      const response = await this.client.post('/auth/refresh', {});
      return response.data;
    } catch (error: any) {
      // Another tab rotated the same refresh token a moment earlier. The new
      // session cookies are shared, so wait briefly and let the caller retry.
      if (error?.response?.status === 409 && error?.response?.data?.code === 'refresh_in_progress') {
        await new Promise((resolve) => window.setTimeout(resolve, 300));
        return { refreshed_elsewhere: true };
      }
      throw error;
    }
  }

  async logout(all_devices = false) {
    const response = await this.client.post('/auth/logout', { all_devices });
    return response.data;
  }

  async getMySessions() {
    const response = await this.client.get('/auth/sessions');
    return response.data;
  }

  async getMySecurityOverview() {
    const response = await this.client.get('/auth/security-overview');
    return response.data as AccountSecurityOverview;
  }

  async revokeMySession(id: number) {
    const response = await this.client.delete(`/auth/sessions/${id}`);
    return response.data;
  }

  async getMe(options?: { silent?: boolean; skipRefresh?: boolean }) {
    const response = await this.client.get('/auth/me', {
      suppressAuthRedirect: Boolean(options?.silent),
      suppressAuthRefresh: Boolean(options?.skipRefresh),
    } as any);
    return response.data;
  }

  async updateMe(data: { first_name?: string; last_name?: string;
    password?: string;
    current_password?: string;
    new_password?: string;
    name?: string;
    display_name?: string | null;
    preferred_language?: 'en' | 'es';
    timezone?: string;
    date_format?: 'MM/DD/YYYY' | 'DD/MM/YYYY';
    theme_preference?: 'light' | 'dark' | 'system';
    people_name_order?: 'first_last' | 'last_first';
    start_page?: string;
    profile_photo?: string | null;
    notify_supervisor_missing_timesheet?: number | boolean;
    notify_pending_approval_days?: number;
    notify_timesheet_status_changes?: number | boolean;
    notify_project_assignment_changes?: number | boolean;
    notify_project_delay_alerts?: number | boolean;
    notify_maintenance_alerts?: number | boolean;
    notify_security_alerts?: number | boolean;
    inapp_timesheet_status_changes?: number | boolean;
    inapp_project_assignment_changes?: number | boolean;
    inapp_project_delay_alerts?: number | boolean;
    inapp_maintenance_alerts?: number | boolean;
    inapp_security_alerts?: number | boolean;
  }) {
    const response = await this.client.patch('/auth/me', data);
    return response.data;
  }

  async getNotifications() {
    const response = await this.client.get('/notifications');
    return response.data;
  }

  async getPersonalHome(): Promise<import('../types').PersonalHomeData> {
    return (await this.client.get('/personal-home')).data;
  }

  async markNotificationRead(id: number) {
    const response = await this.client.post(`/notifications/${id}/read`);
    return response.data;
  }

  async markAllNotificationsRead() {
    const response = await this.client.post('/notifications/read-all');
    return response.data;
  }

  // Supervisor management endpoints (admin only)
  async getAdmins() { return this.peopleApi.getAdmins(); }
  async createAdmin(data: Parameters<PeopleApi['createAdmin']>[0]) { return this.peopleApi.createAdmin(data); }
  async updateAdmin(id: number, data: Parameters<PeopleApi['updateAdmin']>[1]) { return this.peopleApi.updateAdmin(id, data); }
  async toggleAdminActive(id: number) { return this.peopleApi.toggleAdminActive(id); }
  async deleteAdmin(id: number) { return this.peopleApi.deleteAdmin(id); }
  async archiveAdmin(id: number) { return this.peopleApi.archiveAdmin(id); }
  async getSupervisors() { return this.peopleApi.getSupervisors(); }
  async createSupervisor(data: Parameters<PeopleApi['createSupervisor']>[0]) { return this.peopleApi.createSupervisor(data); }
  async updateSupervisor(id: number, data: Parameters<PeopleApi['updateSupervisor']>[1]) { return this.peopleApi.updateSupervisor(id, data); }
  async toggleSupervisorActive(id: number) { return this.peopleApi.toggleSupervisorActive(id); }
  async deleteSupervisor(id: number, force?: boolean) { return this.peopleApi.deleteSupervisor(id, force); }
  async archiveSupervisor(id: number) { return this.peopleApi.archiveSupervisor(id); }
  async getAccessTemplates() { return this.peopleApi.getAccessTemplates(); }
  async createAccessTemplate(data: {
    account_type: 'admin' | 'supervisor';
    name: string;
    description?: string;
    module_permissions: any;
    sub_module_permissions?: any;
    home_profile?: string | null;
  }) { return this.peopleApi.createAccessTemplate(data); }
  async updateAccessTemplate(id: number, data: {
    name?: string;
    description?: string;
    module_permissions?: any;
    sub_module_permissions?: any;
    home_profile?: string | null;
  }) { return this.peopleApi.updateAccessTemplate(id, data); }
  async deleteAccessTemplate(id: number) { return this.peopleApi.deleteAccessTemplate(id); }
  async getAccessTemplateUsage(id: number) { return this.peopleApi.getAccessTemplateUsage(id); }
  async getUserAssignment(userId: number) { return this.peopleApi.getUserAssignment(userId); }
  async getPeopleMode() { return this.peopleApi.getPeopleMode(); }
  async getPeopleTransferCandidates(q: string) { return this.peopleApi.getPeopleTransferCandidates(q); }
  async setPeopleMode(enabled: boolean) { return this.peopleApi.setPeopleMode(enabled); }
  async getPeopleAssignments(id: number) { return this.peopleApi.getPeopleAssignments(id); }
  async getPeopleStaffingBoard(week_start_date?: string) { return this.peopleApi.getPeopleStaffingBoard(week_start_date); }
  async savePeopleAssignment(id: number, data: Parameters<PeopleApi['savePeopleAssignment']>[1]) { return this.peopleApi.savePeopleAssignment(id, data); }
  async getTimesheetWorkerCandidates(project_id: number, week_start_date: string) { return this.peopleApi.getTimesheetWorkerCandidates(project_id, week_start_date); }
  async enableTimesheetWorkerForWeek(data: Parameters<PeopleApi['enableTimesheetWorkerForWeek']>[0]) { return this.peopleApi.enableTimesheetWorkerForWeek(data); }

  // Timesheet endpoints
  async createTimesheet(week_start_date: string, rows: any[], supervisor_id?: number, project_id?: number | null, template_id?: number) { return this.timesheetApi.createTimesheet(week_start_date, rows, supervisor_id, project_id, template_id); }
  async getTimesheets(params?: { week_start?: string; week_end?: string }) { return this.timesheetApi.getTimesheets(params); }
  async getTimesheetsPaginated(params: { limit: number; before_date?: string; before_id?: number; supervisor_id?: number; project_id?: number }) { return this.timesheetApi.getTimesheetsPaginated(params); }
  async getTimesheetDetail(id: number) { return this.timesheetApi.getTimesheetDetail(id); }
  async getTimesheetOverlaps(id: number): Promise<{ overlaps: TimesheetOverlap[] }> {
    return (await this.client.get(`/timesheets/${id}/overlaps`)).data;
  }
  async exportTimesheet(id: number, language = 'en') { return this.timesheetApi.exportTimesheet(id, language); }
  async updateTimesheetRows(id: number, rows: any[], expectedUpdatedAt?: string) { return this.timesheetApi.updateTimesheetRows(id, rows, expectedUpdatedAt); }
  async updateTimesheetWeek(id: number, week_start_date: string, expectedUpdatedAt?: string) { return this.timesheetApi.updateTimesheetWeek(id, week_start_date, expectedUpdatedAt); }
  async submitTimesheet(id: number) { return this.timesheetApi.submitTimesheet(id); }
  async approveTimesheet(id: number) { return this.timesheetApi.approveTimesheet(id); }
  async rejectTimesheet(id: number, rejection_reason?: string) { return this.timesheetApi.rejectTimesheet(id, rejection_reason); }
  async reopenTimesheet(id: number, reopen_reason: string) { return this.timesheetApi.reopenTimesheet(id, reopen_reason); }
  async deleteTimesheet(id: number) { return this.timesheetApi.deleteTimesheet(id); }
  async getTimesheetHistory(id: number) { return this.timesheetApi.getTimesheetHistory(id); }
  async deleteTimesheetRow(rowId: number) { return this.timesheetApi.deleteTimesheetRow(rowId); }
  async getTimesheetDashboardSummary(options?: { suppressAuthRedirect?: boolean }) { return this.timesheetApi.getTimesheetDashboardSummary(options); }
  // Project endpoints
  async getProjectDivisions() {
    return (await this.client.get('/project-divisions')).data as import('../types').ProjectDivision[];
  }
  async createProjectDivision(data: { name: string; project_ids: number[] }) {
    return (await this.client.post('/project-divisions', data)).data as import('../types').ProjectDivision;
  }
  async getProjects(options?: { includeArchived?: boolean }) {
    const response = await this.client.get('/projects', {
      params: options?.includeArchived ? { include_archived: '1' } : undefined,
    });
    return response.data;
  }

  async getAllProjectSummaries(options?: { suppressAuthRedirect?: boolean }) {
    const response = await this.client.get('/projects/summaries/all', {
      suppressAuthRedirect: Boolean(options?.suppressAuthRedirect),
    } as any);
    return response.data;
  }

  async getDashboardHealthSummary(options?: { suppressAuthRedirect?: boolean }) {
    const response = await this.client.get('/projects/dashboard-health-summary', {
      suppressAuthRedirect: Boolean(options?.suppressAuthRedirect),
    } as any);
    return response.data;
  }

  async getProjectSummary(id: number, workspaceOnly = false) {
    const response = await this.client.get(`/projects/${id}`, { params: workspaceOnly ? { view: 'workspace' } : undefined });
    return response.data;
  }

  async createProjectBudgetCategory(projectId: number, data: {
    name: string;
    code?: string | null;
    budget_amount: number;
    overhead_percentage: number;
    is_default?: boolean;
    is_active?: boolean;
    display_order?: number;
  }) {
    const response = await this.client.post(`/projects/${projectId}/budget-categories`, data);
    return response.data;
  }

  async updateProjectBudgetCategory(projectId: number, categoryId: number, data: {
    name?: string;
    code?: string | null;
    budget_amount?: number;
    overhead_percentage?: number;
    is_default?: boolean;
    is_active?: boolean;
    display_order?: number;
  }) {
    const response = await this.client.patch(`/projects/${projectId}/budget-categories/${categoryId}`, data);
    return response.data;
  }

  async createProjectRentalCrew(projectId: number, data: {
    supplier_id: number;
    budget_category_id?: number | null;
    company_name?: string;
    crew_name?: string | null;
    regular_rate: number;
    ot_policy?: 'company_multiplier' | 'same_rate' | 'custom_rate';
    custom_ot_rate?: number | null;
    overhead_mode?: 'project_default' | 'none' | 'custom';
    overhead_percentage?: number;
    start_date?: string | null;
    end_date?: string | null;
    status?: 'planned' | 'active' | 'inactive' | 'complete';
  }) {
    const response = await this.client.post(`/projects/${projectId}/rental-crews`, data);
    return response.data;
  }

  async updateProjectRentalCrew(projectId: number, crewId: number, data: {
    supplier_id?: number;
    budget_category_id?: number | null;
    company_name?: string;
    crew_name?: string | null;
    regular_rate?: number;
    ot_policy?: 'company_multiplier' | 'same_rate' | 'custom_rate';
    custom_ot_rate?: number | null;
    overhead_mode?: 'project_default' | 'none' | 'custom';
    overhead_percentage?: number;
    start_date?: string | null;
    end_date?: string | null;
    status?: 'planned' | 'active' | 'inactive' | 'complete';
    rate_effective_date?: string;
    rate_note?: string | null;
  }) {
    const response = await this.client.patch(`/projects/${projectId}/rental-crews/${crewId}`, data);
    return response.data;
  }

  async removeProjectRentalCrew(projectId: number, crewId: number) {
    const response = await this.client.delete(`/projects/${projectId}/rental-crews/${crewId}`);
    return response.data as {
      success: boolean;
      deleted: boolean;
      archived: boolean;
      ungrouped_workers: number;
    };
  }

  async addProjectRentalCrewWorkers(projectId: number, crewId: number, data: { names: string[]; position?: string | null }) {
    const response = await this.client.post(`/projects/${projectId}/rental-crews/${crewId}/workers`, data);
    return response.data as { workers: Array<{ id: number; name: string }>; skipped_duplicates: number };
  }

  async createProject(data: {
    name: string;
    labor_budget?: number | null;
    overhead_percentage?: number;
    code?: string;
    schedule_start_date?: string;
    schedule_end_date?: string | null;
    minimum_hourly_rate?: number | null;
    client?: string | null;
    location?: string | null;
    schedule_sync_mode?: 'linked' | 'manual' | 'hybrid';
  }) {
    const response = await this.client.post('/projects', data);
    return response.data;
  }

  async updateProject(id: number, data: {
    name?: string;
    labor_budget?: number | null;
    minimum_hourly_rate?: number | null;
    overhead_percentage?: number;
    rented_overhead_mode?: 'none' | 'budget';
    status?: string;
    code?: string | null;
    schedule_start_date?: string | null;
    schedule_end_date?: string | null;
    client?: string | null;
    location?: string | null;
    progress_percent?: number;
    schedule_sync_mode?: 'linked' | 'manual' | 'hybrid';
    use_global_item_weights?: boolean;
    use_area_weights?: boolean;
    disabled_modules?: import('../types').ProjectWorkspaceModuleKey[];
  }) {
    const response = await this.client.patch(`/projects/${id}`, data);
    return response.data;
  }

  async setProjectArchived(id: number, archived: boolean, overrideReason?: string) {
    const response = await this.client.patch(`/projects/${id}/archive`, { archived, override_reason: overrideReason || undefined });
    return response.data as Pick<import('../types').Project, 'id' | 'is_archived' | 'archived_at' | 'archived_by' | 'updated_at'>;
  }

  async getProjectDropboxFolder(projectId: number) {
    const response = await this.client.get(`/projects/${projectId}/dropbox-folder`);
    return response.data as {
      project_id: number;
      dropbox_folder_path: string | null;
      dropbox_folder_id: string | null;
      default_path: string;
    };
  }

  async createDefaultProjectDropboxFolder(projectId: number) {
    const response = await this.client.post(`/projects/${projectId}/dropbox-folder/default`);
    return response.data as {
      project_id: number;
      dropbox_folder_path: string;
      dropbox_folder_id: string | null;
      default_path: string;
    };
  }

  async updateProjectDropboxFolder(projectId: number, data: { dropbox_folder_path: string }) {
    const response = await this.client.patch(`/projects/${projectId}/dropbox-folder`, data);
    return response.data as {
      project_id: number;
      dropbox_folder_path: string;
      dropbox_folder_id: string | null;
    };
  }

  async createChangeOrder(projectId: number, data: { title: string; description?: string; labor_budget: number; status?: string; budget_category_id?: number | null }) {
    const response = await this.client.post(`/projects/${projectId}/change-orders`, data);
    return response.data;
  }

  async updateChangeOrder(projectId: number, changeOrderId: number, data: { title?: string; description?: string; labor_budget?: number; status?: string; budget_category_id?: number | null }) {
    const response = await this.client.patch(`/projects/${projectId}/change-orders/${changeOrderId}`, data);
    return response.data;
  }

  async deleteChangeOrder(projectId: number, changeOrderId: number) {
    const response = await this.client.delete(`/projects/${projectId}/change-orders/${changeOrderId}`);
    return response.data;
  }

  async getProjectTicketWorkOrders(projectId: number) {
    const response = await this.client.get(`/projects/${projectId}/ticket-work-orders`);
    return response.data as import('../types').ProjectTicketRecoverySummary;
  }

  async getAllProjectTicketWorkOrders() {
    const response = await this.client.get('/ticket-work-orders');
    return response.data as import('../types').ProjectTicketRecoverySummary;
  }

  async createProjectTicketWorkOrder(projectId: number, data: {
    reference_number?: string | null;
    title: string;
    description?: string | null;
    regular_billing_rate?: number | null;
    ot_billing_rate?: number | null;
    include_overhead?: boolean;
  }) {
    const response = await this.client.post(`/projects/${projectId}/ticket-work-orders`, data);
    return response.data as import('../types').ProjectTicketWorkOrder;
  }

  async updateProjectTicketWorkOrder(projectId: number, workOrderId: number, data: {
    reference_number?: string | null;
    title?: string;
    description?: string | null;
    billing_status?: import('../types').ProjectTicketBillingStatus;
    regular_billing_rate?: number | null;
    ot_billing_rate?: number | null;
    invoice_number?: string | null;
    invoice_amount?: number | null;
    note?: string | null;
  }) {
    const response = await this.client.patch(
      `/projects/${projectId}/ticket-work-orders/${workOrderId}`,
      data,
    );
    return response.data as import('../types').ProjectTicketWorkOrder;
  }

  async setProjectTicketArchived(projectId: number, workOrderId: number, archived: boolean) {
    const response = await this.client.patch(
      `/projects/${projectId}/ticket-work-orders/${workOrderId}/archive`,
      { archived },
    );
    return response.data as import('../types').ProjectTicketWorkOrder;
  }

  async getDeletedProjectTickets(projectId: number) {
    const response = await this.client.get(`/projects/${projectId}/ticket-work-orders/deleted`);
    return response.data as { items: import('../types').DeletedProjectTicket[] };
  }

  async softDeleteProjectTicketWorkOrder(projectId: number, workOrderId: number, reason: string) {
    const response = await this.client.post(`/projects/${projectId}/ticket-work-orders/${workOrderId}/delete`, { reason });
    return response.data as { id: number; deleted: boolean; recalculation_pending?: string[] };
  }

  async restoreProjectTicketWorkOrder(projectId: number, workOrderId: number) {
    const response = await this.client.post(`/projects/${projectId}/ticket-work-orders/${workOrderId}/restore`);
    return response.data as { id: number; restored: boolean; recalculation_pending?: string[] };
  }

  async deleteProjectTicketWorkOrder(projectId: number, workOrderId: number) {
    const response = await this.client.delete(
      `/projects/${projectId}/ticket-work-orders/${workOrderId}`,
    );
    return response.data as {
      deleted: boolean;
      id: number;
      recalculation_pending?: string[];
    };
  }

  async reviewProjectTicketWorkOrder(
    projectId: number,
    workOrderId: number,
    action: 'submit' | 'approve' | 'return',
    note?: string,
  ) {
    const segment = action === 'return' ? 'return-for-correction' : action;
    const response = await this.client.post(
      `/projects/${projectId}/ticket-work-orders/${workOrderId}/${segment}`,
      note ? { note } : {},
    );
    return response.data as import('../types').ProjectTicketWorkOrder;
  }

  // ── Guided mode (simple foreman dashboard) ───────────────────────────────
  async getGuidedStatus() {
    const response = await this.client.get('/guided/me');
    return response.data as import('../types').GuidedStatus;
  }

  async getSupervisorGuidedSettings(supervisorId: number) {
    const response = await this.client.get(`/supervisors/${supervisorId}/guided-settings`);
    return response.data as {
      settings: import('../types').GuidedSettings;
      template_key: string | null;
      template_settings: import('../types').GuidedSettings | null;
    };
  }

  async updateSupervisorGuidedSettings(supervisorId: number, settings: import('../types').GuidedSettings) {
    const response = await this.client.put(`/supervisors/${supervisorId}/guided-settings`, { settings });
    return response.data.settings as import('../types').GuidedSettings;
  }

  async getDocumentFolderNames() {
    const response = await this.client.get('/document-folder-access/folder-names');
    return response.data.folders as Array<{ name: string; key: string; project_count: number }>;
  }

  async getSupervisorDocumentFolderAccess(supervisorId: number) {
    const response = await this.client.get(`/supervisors/${supervisorId}/document-folder-access`);
    return response.data as {
      access: import('../types').DocumentFolderAccess;
      template_key: string | null;
      template_access: import('../types').DocumentFolderAccess | null;
    };
  }

  async updateSupervisorDocumentFolderAccess(supervisorId: number, access: import('../types').DocumentFolderAccess) {
    const response = await this.client.put(`/supervisors/${supervisorId}/document-folder-access`, { access });
    return response.data.access as import('../types').DocumentFolderAccess;
  }

  async getDocumentFolderAccessTemplate(templateKey: string) {
    const response = await this.client.get(`/document-folder-access/template-settings/${encodeURIComponent(templateKey)}`);
    return response.data as { access: import('../types').DocumentFolderAccess | null; assigned_count: number };
  }

  async updateDocumentFolderAccessTemplate(templateKey: string, access: import('../types').DocumentFolderAccess, applyToAssigned: boolean) {
    const response = await this.client.put(`/document-folder-access/template-settings/${encodeURIComponent(templateKey)}`, {
      access,
      apply_to_assigned: applyToAssigned,
    });
    return response.data as { access: import('../types').DocumentFolderAccess; applied_count: number };
  }

  async getGuidedTemplateSettings(templateKey: string) {
    const response = await this.client.get(`/guided/template-settings/${encodeURIComponent(templateKey)}`);
    return response.data as { settings: import('../types').GuidedSettings | null; assigned_count: number };
  }

  async updateGuidedTemplateSettings(templateKey: string, settings: import('../types').GuidedSettings, applyToAssigned: boolean) {
    const response = await this.client.put(`/guided/template-settings/${encodeURIComponent(templateKey)}`, {
      settings,
      apply_to_assigned: applyToAssigned,
    });
    return response.data as { settings: import('../types').GuidedSettings; applied_count: number };
  }

  async getGuidedDocumentTasks(includeArchived = false) {
    const response = await this.client.get('/guided/document-tasks', { params: includeArchived ? { include_archived: '1' } : undefined });
    return response.data.tasks as import('../types').GuidedDocumentTask[];
  }

  async createGuidedDocumentTask(input: { name: string; frequency: 'daily' | 'weekly'; folder_name: string; instructions?: string | null }) {
    const response = await this.client.post('/guided/document-tasks', input);
    return response.data.task as import('../types').GuidedDocumentTask;
  }

  async updateGuidedDocumentTask(taskId: number, input: Partial<{ name: string; frequency: 'daily' | 'weekly'; folder_name: string; instructions: string | null; is_archived: boolean }>) {
    const response = await this.client.patch(`/guided/document-tasks/${taskId}`, input);
    return response.data.task as import('../types').GuidedDocumentTask;
  }

  async confirmGuidedTimesheetDay(projectId: number, workDate: string, timesheetId: number) {
    await this.client.post(`/guided/projects/${projectId}/timesheet-days`, { work_date: workDate, timesheet_id: timesheetId });
  }

  // ── Project tasks ──────────────────────────────────────────────────────────
  async getMyTasks(date?: string | null) {
    const response = await this.client.get('/tasks/me', { params: date ? { date } : undefined });
    return response.data as import('../types').MyTasksStatus;
  }

  async dismissTaskNotice(projectId: number, taskId: number, date: string) {
    await this.client.post('/tasks/dismiss', { project_id: projectId, task_id: taskId, date });
  }

  async getProjectTasks(projectId: number) {
    const response = await this.client.get(`/projects/${projectId}/tasks`);
    return response.data.tasks as import('../types').ProjectTask[];
  }

  async getProjectTasksToday(projectId: number) {
    const response = await this.client.get(`/projects/${projectId}/tasks/today`);
    return response.data as import('../types').ProjectTasksToday;
  }

  async createProjectTask(projectId: number, input: import('../types').ProjectTaskInput) {
    const response = await this.client.post(`/projects/${projectId}/tasks`, input);
    return response.data.task as import('../types').ProjectTask;
  }

  async updateProjectTask(projectId: number, taskId: number, input: import('../types').ProjectTaskInput) {
    const response = await this.client.patch(`/projects/${projectId}/tasks/${taskId}`, input);
    return response.data.task as import('../types').ProjectTask;
  }

  async deleteProjectTask(projectId: number, taskId: number) {
    await this.client.delete(`/projects/${projectId}/tasks/${taskId}`);
  }

  async setProjectNoWorkDay(projectId: number, date: string, noWork: boolean) {
    if (noWork) await this.client.put(`/projects/${projectId}/no-work-days/${date}`);
    else await this.client.delete(`/projects/${projectId}/no-work-days/${date}`);
  }

  async getMyPeople() {
    const response = await this.client.get('/workforce/people');
    return response.data as import('../types').MyPeopleList;
  }

  /** Uploads one photo or PDF for a project document task (SPA, Toolbox Talk…) for one day of this week. */
  async uploadProjectTaskDocument(
    projectId: number,
    taskId: number,
    taskDate: string,
    file: File,
    contentType: string,
    onProgress?: (percent: number) => void,
  ) {
    const base = `/projects/${projectId}/tasks/${taskId}`;
    const session = await this.client.post(
      `${base}/upload-session`,
      { file_name: file.name, content_type: contentType, file_size: file.size, task_date: taskDate },
      { timeout: 60000 },
    );
    const uploadToken = String(session.data?.upload_token || '');
    const chunkSize = Number(session.data?.chunk_size || 0);
    if (!uploadToken || !Number.isSafeInteger(chunkSize) || chunkSize <= 0) {
      throw new Error('The upload could not be started');
    }
    let offset = 0;
    while (offset < file.size) {
      const end = Math.min(file.size, offset + chunkSize);
      const isFinal = end === file.size;
      const chunk = await file.slice(offset, end).arrayBuffer();
      const response = await this.client.post(`${base}/uploads/chunk`, chunk, {
        timeout: 90000,
        headers: {
          'Content-Type': 'application/octet-stream',
          'X-Document-Upload-Token': uploadToken,
          'X-Upload-Offset': String(offset),
          'X-Upload-Final': isFinal ? '1' : '0',
        },
        transformRequest: (data) => data,
      });
      offset = end;
      onProgress?.(Math.round((offset / file.size) * 100));
      if (isFinal) return response.data;
      if (Number(response.data?.next_offset) !== offset) throw new Error('The upload was interrupted');
    }
    throw new Error('The file was empty');
  }

  /** Uploads one photo or PDF for a guided document task into its folder of the project. */
  async uploadGuidedDocument(
    projectId: number,
    taskId: number,
    file: File,
    contentType: string,
    onProgress?: (percent: number) => void,
  ) {
    const base = `/projects/${projectId}/document-tasks/${taskId}`;
    const session = await this.client.post(
      `/guided${base}/upload-session`,
      { file_name: file.name, content_type: contentType, file_size: file.size },
      { timeout: 60000 },
    );
    const uploadToken = String(session.data?.upload_token || '');
    const chunkSize = Number(session.data?.chunk_size || 0);
    if (!uploadToken || !Number.isSafeInteger(chunkSize) || chunkSize <= 0) {
      throw new Error('The upload could not be started');
    }
    let offset = 0;
    while (offset < file.size) {
      const end = Math.min(file.size, offset + chunkSize);
      const isFinal = end === file.size;
      const chunk = await file.slice(offset, end).arrayBuffer();
      const response = await this.client.post(`/guided${base}/uploads/chunk`, chunk, {
        timeout: 90000,
        headers: {
          'Content-Type': 'application/octet-stream',
          'X-Document-Upload-Token': uploadToken,
          'X-Upload-Offset': String(offset),
          'X-Upload-Final': isFinal ? '1' : '0',
        },
        transformRequest: (data) => data,
      });
      offset = end;
      onProgress?.(Math.round((offset / file.size) * 100));
      if (isFinal) return response.data;
      if (Number(response.data?.next_offset) !== offset) throw new Error('The upload was interrupted');
    }
    throw new Error('The file was empty');
  }

  // ── Free-form project reports ─────────────────────────────────────────────
  async getProjectFreeReportOptions(projectId: number) {
    const response = await this.client.get(`/projects/${projectId}/free-reports/options`);
    return response.data as import('../types').ProjectFreeReportOptions;
  }

  async saveProjectFreeReportLists(projectId: number, lists: import('../types').ProjectFreeReportLists) {
    const response = await this.client.put(`/projects/${projectId}/free-reports/lists`, lists);
    return response.data as import('../types').ProjectFreeReportLists;
  }

  async getProjectFreeReports(projectId: number) {
    const response = await this.client.get(`/projects/${projectId}/free-reports`);
    return response.data as { reports: import('../types').ProjectFreeReport[]; can_view_all: boolean };
  }

  async getReportFeed(type: 'all' | 'field' | 'free' = 'all', cursor?: string) {
    const response = await this.client.get('/reports/feed', { params: { type, cursor } });
    return response.data as import('../types').ReportFeed;
  }

  async getProjectFreeReport(projectId: number, reportId: number) {
    const response = await this.client.get(`/projects/${projectId}/free-reports/${reportId}`);
    return response.data as import('../types').ProjectFreeReportDetail;
  }

  async createProjectFreeReport(projectId: number, input: import('../types').ProjectFreeReportHeaderInput) {
    const response = await this.client.post(`/projects/${projectId}/free-reports`, input);
    return response.data.report as import('../types').ProjectFreeReport;
  }

  async updateProjectFreeReport(projectId: number, reportId: number, input: Partial<import('../types').ProjectFreeReportHeaderInput>) {
    const response = await this.client.patch(`/projects/${projectId}/free-reports/${reportId}`, input);
    return response.data.report as import('../types').ProjectFreeReport;
  }

  async deleteProjectFreeReport(projectId: number, reportId: number) {
    await this.client.delete(`/projects/${projectId}/free-reports/${reportId}`);
  }

  async setProjectFreeReportStatus(projectId: number, reportId: number, action: 'submit' | 'reopen') {
    const response = await this.client.post(`/projects/${projectId}/free-reports/${reportId}/${action}`, {});
    return response.data.report as import('../types').ProjectFreeReport;
  }

  async addProjectFreeReportEntries(projectId: number, reportId: number, rows: import('../types').ProjectFreeReportEntryInput[]) {
    const response = await this.client.post(`/projects/${projectId}/free-reports/${reportId}/entries`, { rows });
    return response.data.entries as import('../types').ProjectFreeReportEntry[];
  }

  async updateProjectFreeReportEntry(projectId: number, reportId: number, entryId: number, row: import('../types').ProjectFreeReportEntryInput) {
    const response = await this.client.patch(`/projects/${projectId}/free-reports/${reportId}/entries/${entryId}`, row);
    return response.data.entry as import('../types').ProjectFreeReportEntry;
  }

  async deleteProjectFreeReportEntry(projectId: number, reportId: number, entryId: number) {
    await this.client.delete(`/projects/${projectId}/free-reports/${reportId}/entries/${entryId}`);
  }

  /** Uploads one photo or PDF through the chunked Dropbox upload session. */
  async markProjectFreeReportAttachmentFinancial(projectId: number, reportId: number, attachmentId: number) {
    const response = await this.client.patch(`/projects/${projectId}/free-reports/${reportId}/attachments/${attachmentId}/classification`, { content_classification: 'financial' });
    return response.data;
  }

  async markProjectRfiAttachmentFinancial(projectId: number, rfiId: number, attachmentId: number) {
    const response = await this.client.patch(`/projects/${projectId}/rfis/${rfiId}/attachments/${attachmentId}/classification`, { content_classification: 'financial' });
    return response.data;
  }

  async uploadProjectFreeReportAttachment(
    projectId: number,
    reportId: number,
    file: File,
    contentType: string,
    onProgress?: (percent: number) => void,
    classification?: 'operational' | 'financial',
    zone?: string | null,
  ) {
    const session = await this.client.post(
      `/projects/${projectId}/free-reports/${reportId}/attachments/upload-session`,
      { file_name: file.name, content_type: contentType, file_size: file.size, zone, ...(classification ? { content_classification: classification } : {}) },
      { timeout: 60000 },
    );
    const uploadToken = String(session.data?.upload_token || '');
    const chunkSize = Number(session.data?.chunk_size || 0);
    if (!uploadToken || !Number.isSafeInteger(chunkSize) || chunkSize <= 0) {
      throw new Error('The attachment upload session could not be started');
    }
    let offset = 0;
    while (offset < file.size) {
      const end = Math.min(file.size, offset + chunkSize);
      const isFinal = end === file.size;
      const chunk = await file.slice(offset, end).arrayBuffer();
      const response = await this.client.post(
        `/projects/${projectId}/free-reports/${reportId}/attachments/uploads/chunk`,
        chunk,
        {
          timeout: 90000,
          headers: {
            'Content-Type': 'application/octet-stream',
            'X-Free-Report-Upload-Token': uploadToken,
            'X-Upload-Offset': String(offset),
            'X-Upload-Final': isFinal ? '1' : '0',
          },
          transformRequest: (data) => data,
        },
      );
      offset = end;
      onProgress?.(Math.round((offset / file.size) * 100));
      if (isFinal) return response.data.attachment as import('../types').ProjectFreeReportAttachment;
      if (Number(response.data?.next_offset) !== offset) throw new Error('The attachment upload was interrupted');
    }
    throw new Error('The attachment was empty');
  }

  async setReportAttachmentZone(kind: 'free' | 'field', projectId: number, reportId: number, attachmentId: number, zone: string | null) {
    const path = kind === 'free' ? 'free-reports' : 'field-reports';
    const collection = kind === 'free' ? 'attachments' : 'photos';
    const response = await this.client.patch(`/projects/${projectId}/${path}/${reportId}/${collection}/${attachmentId}/zone`, { zone });
    return response.data as { id: number; zone: string | null };
  }

  /** Saves the markup layer over a report photo; the original image is not rewritten. */
  async saveReportPhotoMarkups(kind: 'free' | 'field', projectId: number, reportId: number, attachmentId: number, markups: import('../types').ReportPhotoMarkup[], expectedRevision: number) {
    const path = kind === 'free' ? 'free-reports' : 'field-reports';
    const collection = kind === 'free' ? 'attachments' : 'photos';
    const response = await this.client.put(`/projects/${projectId}/${path}/${reportId}/${collection}/${attachmentId}/markups`, { markups, expected_revision: expectedRevision });
    return response.data as { markups: import('../types').ReportPhotoMarkup[]; markup_revision: number };
  }

  async getReportImageSource(kind: 'free' | 'field', projectId: number, reportId: number, attachmentId: number, signal?: AbortSignal) {
    const path = kind === 'free'
      ? `free-reports/${reportId}/attachments/${attachmentId}/view`
      : `field-reports/${reportId}/photos/${attachmentId}/image-source`;
    const response = await this.client.get(`/projects/${projectId}/${path}`, { responseType: 'blob', signal });
    return response.data as Blob;
  }

  getProjectFreeReportAttachmentUrl(projectId: number, reportId: number, attachmentId: number) {
    return `${API_BASE_URL}/projects/${projectId}/free-reports/${reportId}/attachments/${attachmentId}/view`;
  }

  getProjectFreeReportAttachmentThumbnailUrl(projectId: number, reportId: number, attachmentId: number) {
    return `${API_BASE_URL}/projects/${projectId}/free-reports/${reportId}/attachments/${attachmentId}/thumbnail`;
  }

  async getReportThumbnail(url: string, signal: AbortSignal): Promise<Blob> {
    return (await this.client.get<Blob>(url, { responseType: 'blob', signal })).data;
  }

  async getProjectFreeReportPdfPreview(
    projectId: number,
    reportId: number,
    attachmentId: number,
    signal?: AbortSignal,
    onProgress?: (percent: number) => void,
  ) {
    const response = await this.client.get(
      `/projects/${projectId}/free-reports/${reportId}/attachments/${attachmentId}/pdf-preview`,
      {
        responseType: 'arraybuffer',
        timeout: 90000,
        signal,
        onDownloadProgress: (event) => {
          if (!event.total || event.total <= 0) return;
          onProgress?.(Math.min(100, Math.round((event.loaded / event.total) * 100)));
        },
      },
    );
    return response.data as ArrayBuffer;
  }

  async deleteProjectFreeReportAttachment(projectId: number, reportId: number, attachmentId: number) {
    await this.client.delete(`/projects/${projectId}/free-reports/${reportId}/attachments/${attachmentId}`);
  }

  async createProjectFreeReportPin(projectId: number, reportId: number, attachmentId: number, data: {
    page_number?: number;
    x_ratio: number;
    y_ratio: number;
    comment: string;
  }) {
    const response = await this.client.post(`/projects/${projectId}/free-reports/${reportId}/attachments/${attachmentId}/pins`, data);
    return response.data.pin as import('../types').ProjectFreeReportPin;
  }

  async deleteProjectFreeReportPin(projectId: number, reportId: number, attachmentId: number, pinId: number) {
    await this.client.delete(`/projects/${projectId}/free-reports/${reportId}/attachments/${attachmentId}/pins/${pinId}`);
  }

  async getProjectTicketAttachments(projectId: number, workOrderId: number) {
    const response = await this.client.get(
      `/projects/${projectId}/ticket-work-orders/${workOrderId}/attachments`,
    );
    return (response.data?.attachments || []) as import('../types').ProjectTicketAttachment[];
  }

  async classifyProjectTicketAttachment(projectId: number, workOrderId: number, attachmentId: number) {
    const response = await this.client.patch(`/projects/${projectId}/ticket-work-orders/${workOrderId}/attachments/${attachmentId}/classification`, { content_classification: 'financial' });
    return response.data;
  }

  async startProjectTicketAttachmentUpload(
    projectId: number,
    workOrderId: number,
    input: { file_name: string; content_type: string; file_size: number; content_classification?: 'operational' | 'financial' },
  ) {
    const response = await this.client.post(
      `/projects/${projectId}/ticket-work-orders/${workOrderId}/attachments/upload-session`,
      input,
    );
    return response.data as { attachment_id: number; upload_token: string; chunk_size: number };
  }

  async uploadProjectTicketAttachmentChunk(
    projectId: number,
    workOrderId: number,
    token: string,
    offset: number,
    chunk: Blob,
    isFinal: boolean,
  ) {
    const response = await this.client.post(
      `/projects/${projectId}/ticket-work-orders/${workOrderId}/attachments/uploads/chunk`,
      chunk,
      {
        headers: {
          'Content-Type': 'application/octet-stream',
          'X-Ticket-Attachment-Upload-Token': token,
          'X-Upload-Offset': String(offset),
          'X-Upload-Final': isFinal ? '1' : '0',
        },
      },
    );
    return response.data as { next_offset?: number; attachment?: import('../types').ProjectTicketAttachment };
  }

  async openProjectTicketAttachment(projectId: number, workOrderId: number, attachmentId: number) {
    const response = await this.client.get(
      `/projects/${projectId}/ticket-work-orders/${workOrderId}/attachments/${attachmentId}/open`,
    );
    return { ...response.data, url: resolveApiFileUrl(response.data.url, API_BASE_URL) } as { url: string; file_name: string; content_type: string; file_size: number };
  }

  async deleteProjectTicketAttachment(projectId: number, workOrderId: number, attachmentId: number) {
    await this.client.delete(
      `/projects/${projectId}/ticket-work-orders/${workOrderId}/attachments/${attachmentId}`,
    );
  }

  async getProjectTicketTimeEntries(
    projectId: number,
    workOrderId: number,
    week?: string,
  ) {
    const params = week ? { week } : {};
    const response = await this.client.get(
      `/projects/${projectId}/ticket-work-orders/${workOrderId}/time-entries`,
      { params },
    );
    return response.data as import('../types').ProjectTicketTimeEntriesResponse;
  }

  /** Interactive People, past week only: adds the person to the project for that week. */
  async addProjectTicketPastWeekWorker(projectId: number, workOrderId: number, employeeId: number, week_start_date: string) {
    const response = await this.client.post(
      `/projects/${projectId}/ticket-work-orders/${workOrderId}/past-week-workers`,
      { employee_id: employeeId, week_start_date },
    );
    return response.data as { employee_id: number; week_start_date: string };
  }

  async replaceProjectTicketEntries(
    projectId: number,
    workOrderId: number,
    week_start_date: string,
    rows: Array<{
      employee_id: number;
      work_day: import('../types').ProjectTicketWorkDay;
      regular_hours: number;
      ot_hours: number;
    }>,
  ) {
    const response = await this.client.put(
      `/projects/${projectId}/ticket-work-orders/${workOrderId}/time-entries`,
      { week_start_date, rows },
    );
    return response.data as import('../types').ProjectTicketRecoverySummary;
  }

  async getProjectTicketWorkOrderHistory(projectId: number, workOrderId: number) {
    const response = await this.client.get(
      `/projects/${projectId}/ticket-work-orders/${workOrderId}/history`,
    );
    return response.data as Array<{
      id: number;
      from_status?: import('../types').ProjectTicketBillingStatus | null;
      to_status: import('../types').ProjectTicketBillingStatus;
      note?: string | null;
      performed_by_email?: string | null;
      created_at: string;
    }>;
  }

  async getProjectExternalCosts(projectId: number) {
    const response = await this.client.get(`/projects/${projectId}/external-costs`);
    return response.data;
  }

  async createProjectExternalCost(projectId: number, data: {
    cost_code?: string;
    title: string;
    vendor_name?: string | null;
    description?: string | null;
    budget_amount?: number;
    actual_amount?: number;
    company_overhead_percentage?: number;
    cost_date?: string | null;
    status?: 'planned' | 'active' | 'complete' | 'cancelled';
    affects_production?: boolean;
    rental_crew_id?: number | null;
  }) {
    const response = await this.client.post(`/projects/${projectId}/external-costs`, data);
    return response.data;
  }

  async updateProjectExternalCost(projectId: number, externalCostId: number, data: {
    cost_code?: string;
    title?: string;
    vendor_name?: string | null;
    description?: string | null;
    budget_amount?: number;
    actual_amount?: number;
    company_overhead_percentage?: number;
    cost_date?: string | null;
    status?: 'planned' | 'active' | 'complete' | 'cancelled';
    affects_production?: boolean;
    rental_crew_id?: number | null;
  }) {
    const response = await this.client.patch(`/projects/${projectId}/external-costs/${externalCostId}`, data);
    return response.data;
  }

  async deleteProjectExternalCost(projectId: number, externalCostId: number) {
    const response = await this.client.delete(`/projects/${projectId}/external-costs/${externalCostId}`);
    return response.data;
  }

  async getProjectSwingStage(projectId: number): Promise<import('../types').SwingStageData> {
    return (await this.client.get('/projects/' + projectId + '/swing-stage')).data;
  }
  async setProjectSwingStageRate(projectId: number, unit_price: number, direction?: import('../types').SwingStageDirection) {
    return (await this.client.put('/projects/' + projectId + '/swing-stage/rate', { unit_price, direction })).data;
  }
  async addProjectSwingStageMovements(projectId: number, data: { movement_date: string; quantity: number; unit_price: number; notes: string; request_key: string; direction?: import('../types').SwingStageDirection }) {
    return (await this.client.post('/projects/' + projectId + '/swing-stage/movements', data)).data;
  }
  async voidProjectSwingStageMovement(projectId: number, movementId: number, reason: string) {
    return (await this.client.post('/projects/' + projectId + '/swing-stage/movements/' + movementId + '/void', { reason })).data;
  }
  async correctHistoricalSwingStage(projectId: number, data: { quantity: number; unit_price: number; expected_cost: number; reason: string; confirm: boolean; direction?: import('../types').SwingStageDirection }) {
    return (await this.client.post('/projects/' + projectId + '/swing-stage/historical-conversion', data)).data;
  }

  async getProjectPriorCost(projectId: number) {
    const response = await this.client.get(`/projects/${projectId}/prior-cost`);
    return response.data as { prior_cost: import('../types').HistoricalBaseline | null };
  }

  async upsertProjectPriorCost(projectId: number, data: Partial<import('../types').HistoricalBaseline>) {
    const response = await this.client.put(`/projects/${projectId}/prior-cost`, data);
    return response.data as { prior_cost: import('../types').HistoricalBaseline };
  }

  async deleteProjectPriorCost(projectId: number) {
    const response = await this.client.delete(`/projects/${projectId}/prior-cost`);
    return response.data;
  }

  async getProjectPriorProgress(projectId: number) {
    const response = await this.client.get(`/projects/${projectId}/prior-progress`);
    return response.data as {
      prior_progress: {
        id: number; project_id: number; notes: string | null; prior_hours: number | null;
        updated_by_name: string | null; updated_at: string;
        quantities: Array<{ production_item_id: number; quantity: number; item_name: string; unit: string }>;
      } | null;
    };
  }

  async upsertProjectPriorProgress(projectId: number, data: {
    notes?: string | null;
    prior_hours?: number | null;
    quantities?: Array<{ production_item_id: number; quantity: number }>;
  }) {
    const response = await this.client.put(`/projects/${projectId}/prior-progress`, data);
    return response.data as {
      prior_progress: {
        id: number; project_id: number; notes: string | null; prior_hours: number | null;
        updated_at: string;
        quantities: Array<{ production_item_id: number; quantity: number; item_name: string; unit: string }>;
      };
    };
  }

  async deleteProjectPriorProgress(projectId: number) {
    const response = await this.client.delete(`/projects/${projectId}/prior-progress`);
    return response.data;
  }

  async deleteProject(id: number, force?: boolean) {
    const response = await this.client.delete(`/projects/${id}`, {
      params: force ? { force: 'true' } : undefined,
    });
    return response.data;
  }

  async getProjectSupervisors(projectId: number) {
    const response = await this.client.get(`/projects/${projectId}/supervisors`);
    return response.data;
  }

  async getAssignableSupervisors() {
    const response = await this.client.get('/projects/assignable-supervisors');
    return response.data;
  }

  async addProjectSupervisor(projectId: number, supervisor_id: number) {
    const response = await this.client.post(`/projects/${projectId}/supervisors`, { supervisor_id });
    return response.data;
  }

  async removeProjectSupervisor(projectId: number, supervisorId: number) {
    const response = await this.client.delete(`/projects/${projectId}/supervisors/${supervisorId}`);
    return response.data;
  }

  async createProjectRentalCrewForStaffing(projectId: number, data: {
    supplier_id: number;
    company_name?: string;
    crew_name?: string | null;
    regular_rate?: number;
    overhead_mode?: 'project_default' | 'none' | 'custom';
    overhead_percentage?: number;
  }) {
    const response = await this.client.post(`/projects/${projectId}/rental-crews/staffing`, data);
    return response.data as ProjectRentalCrew;
  }

  async addProjectRentedWorkers(projectId: number, data: {
    names: string[];
    people?: { first_name: string; last_name: string }[];
    rental_crew_id?: number | null;
    position?: string;
    rental_source_note?: string | null;
    rental_rate_override?: number | null;
    rental_overhead_mode?: 'project_default' | 'none' | 'custom';
    rental_overhead_percentage?: number | null;
  }) {
    const response = await this.client.post(`/projects/${projectId}/rental-workers`, data);
    return response.data as { workers: Array<{ id: number; name: string }>; skipped_duplicates: number };
  }

  async getProjectUserAccess(projectId: number) {
    const response = await this.client.get(`/projects/${projectId}/user-access`);
    return response.data as import('../types').ProjectUserAccess[];
  }

  async getProjectAccessCandidates(projectId: number) {
    const response = await this.client.get(`/projects/${projectId}/user-access-candidates`);
    return response.data as import('../types').ProjectAccessCandidate[];
  }

  async upsertProjectUserAccess(projectId: number, userId: number, data: {
    access_profile?: import('../types').ProjectAccessProfile;
    starts_on?: string | null;
    ends_on?: string | null;
  }) {
    const response = await this.client.put(`/projects/${projectId}/user-access/${userId}`, data);
    return response.data as import('../types').ProjectUserAccess;
  }

  async removeProjectUserAccess(projectId: number, userId: number) {
    const response = await this.client.delete(`/projects/${projectId}/user-access/${userId}`);
    return response.data as { success: true };
  }

  // Employee endpoints
  async getEmployees(project_id?: number, include_inactive?: boolean, include_project_scoped?: boolean, separation_status?: 'scheduled' | 'former', week_start_date?: string, profile?: 'timesheet') { return this.peopleApi.getEmployees(project_id, include_inactive, include_project_scoped, separation_status, week_start_date, profile); }
  async searchEmployeeDirectory(project_id: number, q: string) { return this.peopleApi.searchEmployeeDirectory(project_id, q); }
  async getEmployeeRecordsAttention() { return this.peopleApi.getEmployeeRecordsAttention(); }
  async getEmployeeRequests(status = 'pending') { return this.peopleApi.getEmployeeRequests(status); }
  async createEmployeeRequest(data: { first_name?: string; last_name?: string;
    request_type: 'new_employee' | 'assign_existing';
    project_id: number;
    existing_employee_id?: number;
    name?: string;
    position?: string;
    hourly_rate?: number;
    employment_type?: 'direct' | 'contractor' | 'rented';
    contractor_company?: string;
    rental_source_note?: string;
    rental_crew_id?: number;
    use_immediately?: boolean;
  }) { return this.peopleApi.createEmployeeRequest(data); }
  async updateEmployeeRequest(id: number, data: { first_name?: string; last_name?: string;
    name: string;
    position?: string;
    hourly_rate?: number | null;
    employment_type?: 'direct' | 'contractor' | 'rented';
    contractor_company?: string;
    rental_source_note?: string | null;
    rental_crew_id?: number | null;
  }) { return this.peopleApi.updateEmployeeRequest(id, data); }
  async approveEmployeeRequest(id: number) { return this.peopleApi.approveEmployeeRequest(id); }
  async rejectEmployeeRequest(id: number, review_note?: string) { return this.peopleApi.rejectEmployeeRequest(id, review_note); }
  async getRentedWorkerDirectory(list: 'current' | 'legacy' | 'archived' = 'current'): Promise<RentedWorkerDirectory> {
    return (await this.client.get('/people/rented-workers', { params: { list } })).data;
  }
  async getRentalSuppliers(): Promise<import('../types').RentalSupplier[]> {
    return (await this.client.get('/rental-suppliers')).data;
  }
  async createRentalSupplier(name: string): Promise<import('../types').RentalSupplier> {
    return (await this.client.post('/rental-suppliers', { name })).data;
  }
  async updateRentalSupplier(id: number, data: { name?: string; is_active?: boolean }): Promise<import('../types').RentalSupplier> {
    return (await this.client.patch(`/rental-suppliers/${id}`, data)).data;
  }
  async deleteRentalSupplier(id: number): Promise<{ id: number; deleted: true; unlinked_groups: number }> {
    return (await this.client.delete(`/rental-suppliers/${id}`)).data;
  }
  async getRentalCrewsDirectory() { return this.peopleApi.getRentalCrewsDirectory(); }
  // Company tools issued to workers.
  async getProjectWorkerTools(projectId: number, employeeId: number) {
    const response = await this.client.get(`/projects/${projectId}/workforce/${employeeId}/tools`);
    return response.data as import('../types').WorkerToolsResponse;
  }
  async getAvailableWorkerTools(projectId: number) {
    const response = await this.client.get(`/projects/${projectId}/worker-tools/available`);
    return response.data as import('../types').WorkerToolAvailable[];
  }
  async issueWorkerTool(projectId: number, employeeId: number, unitId: number, note?: string) {
    const response = await this.client.post(`/projects/${projectId}/workforce/${employeeId}/tools/issue`, { unit_id: unitId, note });
    return response.data;
  }
  async returnProjectWorkerTool(projectId: number, employeeId: number, unitId: number, outcome: import('../types').WorkerToolReturnOutcome, note?: string) {
    const response = await this.client.post(`/projects/${projectId}/workforce/${employeeId}/tools/${unitId}/return`, { outcome, note });
    return response.data;
  }
  async getEmployeeWorkerTools(employeeId: number) {
    const response = await this.client.get(`/employees/${employeeId}/worker-tools`);
    return response.data as import('../types').WorkerToolsResponse;
  }
  async returnEmployeeWorkerTool(employeeId: number, unitId: number, outcome: import('../types').WorkerToolReturnOutcome, note?: string) {
    const response = await this.client.post(`/employees/${employeeId}/worker-tools/${unitId}/return`, { outcome, note });
    return response.data;
  }
  async getWorkerToolHolds() {
    const response = await this.client.get('/employees/worker-tool-holds');
    return response.data as import('../types').WorkerToolHoldsResponse;
  }

  async getProjectWorkerInfo(projectId: number, employeeId: number) {
    const response = await this.client.get(`/projects/${projectId}/workforce/${employeeId}/info`);
    return response.data as import('../types').ProjectWorkerInfo;
  }

  async getProjectWorkerCredentialAttachment(projectId: number, employeeId: number, credentialId: number) {
    const response = await this.client.get(`/projects/${projectId}/workforce/${employeeId}/credentials/${credentialId}/attachment`);
    return response.data as { attachment_data: string; attachment_filename: string | null };
  }

  async getProjectEmployees(projectId: number) {
    const response = await this.client.get(`/projects/${projectId}/employees`);
    return response.data as import('../types').ProjectEmployee[];
  }

  async getProjectDashboardStructure(projectId: number) {
    const response = await this.client.get(`/projects/${projectId}/dashboard-structure`);
    return response.data as import('../types').ProjectDashboardStructure;
  }

  async updateRentalWorker(projectId: number, workerId: number, data: { first_name?: string; last_name?: string;
    rental_crew_id?: number | null;
    name?: string;
    position?: string | null;
    is_active?: boolean;
    rental_source_note?: string | null;
    rental_rate_override?: number | null;
    rental_overhead_mode?: 'project_default' | 'none' | 'custom';
    rental_overhead_percentage?: number | null;
    rate_effective_date?: string;
    rate_note?: string;
  }) {
    const response = await this.client.patch(`/projects/${projectId}/rental-workers/${workerId}`, data);
    return response.data;
  }

  async deleteProjectRentalWorker(projectId: number, workerId: number) {
    const response = await this.client.delete(`/projects/${projectId}/rental-workers/${workerId}`);
    return response.data as {
      success: true;
      outcome: 'deleted' | 'archived';
      label: string;
      timesheet_record_count: number;
    };
  }

  async toggleProjectRentalWorkerActive(projectId: number, workerId: number) {
    const response = await this.client.post(`/projects/${projectId}/rental-workers/${workerId}/toggle-active`);
    return response.data as { success: true; is_active: boolean };
  }

  async addProjectEmployee(projectId: number, employee_id: number) {
    const response = await this.client.post(`/projects/${projectId}/employees`, { employee_id });
    return response.data;
  }

  async searchAddExistingProjectEmployees(projectId: number, q: string) {
    const response = await this.client.get(`/projects/${projectId}/employees/add-existing/candidates`, { params: { q } });
    return response.data as { project_matches: Array<{ id: number; name: string; position: string }>; other_matches: Array<{ id: number; name: string; position: string; on_other_project: boolean }> };
  }

  async addExistingProjectEmployee(projectId: number, employee_id: number) {
    const response = await this.client.post(`/projects/${projectId}/employees/add-existing`, { employee_id });
    return response.data as { success: true; moved: boolean; previous_project_count: number; leaves_after: string | null };
  }

  async removeProjectEmployee(projectId: number, employeeId: number) {
    const response = await this.client.delete(`/projects/${projectId}/employees/${employeeId}`);
    return response.data;
  }

  async getEmployeeHoursReport(params?: { week_start?: string; week_end?: string; project_id?:number; status_scope?: 'review' | 'approved' }) {
    const response = await this.client.get('/employees/hours-report', { params });
    return response.data;
  }

  async getPayrollPeriodStatus(weekStart:string) { return (await this.client.get('/payroll-period/status',{params:{week_start:weekStart}})).data; }
  async closePayrollPeriod(weekStart:string) { return (await this.client.post('/payroll-period/close',{week_start:weekStart})).data; }
  async reopenPayrollPeriod(weekStart:string,reason:string) { return (await this.client.post('/payroll-period/reopen',{week_start:weekStart,reason})).data; }

  async getSpecialPayments(params: { employee_id:number; week_start:string }) { return (await this.client.get('/employees/special-payments',{params})).data; }
  async createSpecialPayment(data: Record<string,unknown>) { return (await this.client.post('/employees/special-payments',data)).data; }
  async cancelSpecialPayment(id:number,data:Record<string,unknown>) { return (await this.client.post('/employees/special-payments/'+id+'/cancel',data)).data; }

  async getOtOverrideRows(params: { employee_id: number; week_start_date: string }) {
    const response = await this.client.get('/employees/ot-overrides', { params });
    return response.data;
  }

  async setOtOverrides(data: {
    employee_id: number;
    week_start_date: string;
    overrides: { timesheet_row_id: number; ot_hours: number }[];
    preview?: boolean;
    confirm_approved?: boolean;
    preview_token?: string;
  }) {
    const response = await this.client.put('/employees/ot-overrides', data);
    return response.data;
  }

  async clearOtOverrides(params: { employee_id: number; week_start_date: string; preview?: boolean; confirm_approved?: boolean; preview_token?: string }) {
    const response = await this.client.delete('/employees/ot-overrides', { params });
    return response.data;
  }

  async getProductionProjects() {
    const response = await this.client.get('/reports/production/projects');
    return response.data;
  }

  async getProductionItems(projectId?: number | null) {
    const response = await this.client.get('/reports/production/items', {
      params: projectId ? { project_id: projectId } : undefined,
    });
    return response.data;
  }

  async createProductionItem(data: { name: string; unit: string; project_id?: number | null }) {
    const response = await this.client.post('/reports/production/items', data);
    return response.data;
  }

  async updateProductionItem(id: number, data: { name?: string; unit?: string }) {
    const response = await this.client.patch(`/reports/production/items/${id}`, data);
    return response.data;
  }

  async deleteProductionItem(id: number) {
    const response = await this.client.delete(`/reports/production/items/${id}`);
    return response.data;
  }

  async getProductionEntries(params?: { project_id?: number; from?: string; to?: string }) {
    const response = await this.client.get('/reports/production/entries', { params });
    return response.data;
  }

  async createProductionEntry(data: { project_id: number; production_item_id: number; zone_id?: number | null; quantity: number; report_date: string; note?: string }) {
    const response = await this.client.post('/reports/production/entries', data);
    return response.data;
  }

  async deleteProductionEntry(entryId: number) {
    const response = await this.client.delete(`/reports/production/entries/${entryId}`);
    return response.data;
  }

  async getProjectProductionConfig(projectId: number) {
    const response = await this.client.get(`/projects/${projectId}/production-config`);
    return response.data;
  }

  async getProjectReportWorkspace(projectId: number, options?: { suppressAuthRedirect?: boolean }) {
    const response = await this.client.get(`/projects/${projectId}/report-workspace`, {
      suppressAuthRedirect: Boolean(options?.suppressAuthRedirect),
    } as any);
    return response.data;
  }

  async createReportSpaceNode(projectId: number, data: {
    parent_node_id?: number | null;
    legacy_zone_id?: number | null;
    map_region_id?: string | null;
    name: string;
    node_type: 'building' | 'wing' | 'elevation' | 'track' | 'level' | 'zone' | 'subzone' | 'custom';
    workload_points?: number;
  }) {
    const response = await this.client.post(`/projects/${projectId}/report-space-nodes`, data);
    return response.data;
  }

  async updateReportSpaceNode(projectId: number, nodeId: number, data: {
    parent_node_id?: number | null;
    legacy_zone_id?: number | null;
    map_region_id?: string | null;
    name: string;
    node_type: 'building' | 'wing' | 'elevation' | 'track' | 'level' | 'zone' | 'subzone' | 'custom';
    workload_points?: number;
  }) {
    const response = await this.client.patch(`/projects/${projectId}/report-space-nodes/${nodeId}`, data);
    return response.data;
  }

  async deleteReportSpaceNode(projectId: number, nodeId: number) {
    const response = await this.client.delete(`/projects/${projectId}/report-space-nodes/${nodeId}`);
    return response.data;
  }

  async createReportMap(projectId: number, data: {
    title: string;
    image_url: string;
    image_width?: number | null;
    image_height?: number | null;
    notes?: string | null;
  }) {
    const response = await this.client.post(`/projects/${projectId}/report-maps`, data);
    return response.data;
  }

  async updateReportMap(projectId: number, mapId: number, data: {
    title: string;
    image_url: string;
    image_width?: number | null;
    image_height?: number | null;
    notes?: string | null;
  }) {
    const response = await this.client.patch(`/projects/${projectId}/report-maps/${mapId}`, data);
    return response.data;
  }

  async deleteReportMap(projectId: number, mapId: number) {
    const response = await this.client.delete(`/projects/${projectId}/report-maps/${mapId}`);
    return response.data;
  }

  async createReportMapRegion(projectId: number, data: {
    report_map_id: number;
    space_node_id?: number | null;
    label: string;
    fill_color?: string;
    x_percent: number;
    y_percent: number;
    width_percent: number;
    height_percent: number;
  }) {
    const response = await this.client.post(`/projects/${projectId}/report-map-regions`, data);
    return response.data;
  }

  async updateReportMapRegion(projectId: number, regionId: number, data: {
    report_map_id: number;
    space_node_id?: number | null;
    label: string;
    fill_color?: string;
    x_percent: number;
    y_percent: number;
    width_percent: number;
    height_percent: number;
  }) {
    const response = await this.client.patch(`/projects/${projectId}/report-map-regions/${regionId}`, data);
    return response.data;
  }

  async deleteReportMapRegion(projectId: number, regionId: number) {
    const response = await this.client.delete(`/projects/${projectId}/report-map-regions/${regionId}`);
    return response.data;
  }

  async createReportScopeItem(projectId: number, data: {
    space_node_id?: number | null;
    production_item_id?: number | null;
    unit_override?: string | null;
    quantity_mode?: 'quantity' | 'surface' | 'both';
    target_quantity?: number;
    planned_rate_per_person_day?: number | null;
    planned_crew_size?: number | null;
    planned_duration_days?: number | null;
    weight?: number | null;
    baseline_locked?: boolean;
  }) {
    const response = await this.client.post(`/projects/${projectId}/report-scope-items`, data);
    return response.data;
  }

  async updateReportScopeItem(projectId: number, scopeItemId: number, data: {
    unit_override?: string | null;
    quantity_mode?: 'quantity' | 'surface' | 'both';
    target_quantity?: number;
    planned_rate_per_person_day?: number | null;
    planned_crew_size?: number | null;
    planned_duration_days?: number | null;
    weight?: number | null;
    baseline_locked?: boolean;
  }) {
    const response = await this.client.patch(`/projects/${projectId}/report-scope-items/${scopeItemId}`, data);
    return response.data;
  }

  async deleteReportScopeItem(projectId: number, scopeItemId: number) {
    const response = await this.client.delete(`/projects/${projectId}/report-scope-items/${scopeItemId}`);
    return response.data;
  }

  async createReportDailyLog(projectId: number, data: {
    production_item_id: number;
    space_node_id?: number | null;
    quantity: number;
    manpower_count?: number | null;
    report_date: string;
    note?: string | null;
    external_link?: string | null;
    daily_report_reference?: string | null;
    image_urls?: string[];
  }) {
    const response = await this.client.post(`/projects/${projectId}/report-daily-logs`, data);
    return response.data;
  }

  async updateReportDailyLog(projectId: number, logId: number, data: {
    space_node_id?: number | null;
    quantity: number;
    manpower_count?: number | null;
    report_date: string;
    note?: string | null;
    external_link?: string | null;
    daily_report_reference?: string | null;
    image_urls?: string[];
  }) {
    const response = await this.client.patch(`/projects/${projectId}/report-daily-logs/${logId}`, data);
    return response.data;
  }

  async deleteReportDailyLog(projectId: number, logId: number) {
    const response = await this.client.delete(`/projects/${projectId}/report-daily-logs/${logId}`);
    return response.data;
  }

  async createProjectReportSection(projectId: number, data: {
    title: string;
    metric_label: string;
    unit: string;
    accent_color: string;
    linked_production_item_id?: number | null;
  }) {
    const response = await this.client.post(`/projects/${projectId}/report-sections`, data);
    return response.data;
  }

  async updateProjectReportSection(projectId: number, sectionId: number, data: {
    title: string;
    metric_label: string;
    unit: string;
    accent_color: string;
    linked_production_item_id?: number | null;
  }) {
    const response = await this.client.patch(`/projects/${projectId}/report-sections/${sectionId}`, data);
    return response.data;
  }

  async deleteProjectReportSection(projectId: number, sectionId: number) {
    const response = await this.client.delete(`/projects/${projectId}/report-sections/${sectionId}`);
    return response.data;
  }

  async createProjectReportRow(projectId: number, sectionId: number, data: {
    area: string;
    type_label?: string;
    target_quantity?: number;
    installed_quantity?: number;
  }) {
    const response = await this.client.post(`/projects/${projectId}/report-sections/${sectionId}/rows`, data);
    return response.data;
  }

  async updateProjectReportRow(projectId: number, rowId: number, data: {
    area: string;
    type_label?: string;
    target_quantity?: number;
    installed_quantity?: number;
  }) {
    const response = await this.client.patch(`/projects/${projectId}/report-rows/${rowId}`, data);
    return response.data;
  }

  async deleteProjectReportRow(projectId: number, rowId: number) {
    const response = await this.client.delete(`/projects/${projectId}/report-rows/${rowId}`);
    return response.data;
  }

  async createProjectReportIssue(projectId: number, data: {
    area?: string;
    problem: string;
    impact?: string;
    responsible?: string;
    status?: 'open' | 'monitoring' | 'resolved' | 'blocked';
    note?: string;
  }) {
    const response = await this.client.post(`/projects/${projectId}/report-issues`, data);
    return response.data;
  }

  async updateProjectReportIssue(projectId: number, issueId: number, data: {
    area?: string;
    problem: string;
    impact?: string;
    responsible?: string;
    status?: 'open' | 'monitoring' | 'resolved' | 'blocked';
    note?: string;
  }) {
    const response = await this.client.patch(`/projects/${projectId}/report-issues/${issueId}`, data);
    return response.data;
  }

  async deleteProjectReportIssue(projectId: number, issueId: number) {
    const response = await this.client.delete(`/projects/${projectId}/report-issues/${issueId}`);
    return response.data;
  }

  async upsertProjectProductionItem(projectId: number, itemId: number, data: { target_quantity?: number; unit_override?: string | null; global_weight?: number | null }) {
    const response = await this.client.put(`/projects/${projectId}/production-config/${itemId}`, data);
    return response.data;
  }

  async deleteProjectProductionItem(projectId: number, itemId: number) {
    const response = await this.client.delete(`/projects/${projectId}/production-config/${itemId}`);
    return response.data;
  }

  // Zone management
  async getProductionZones(projectId: number) {
    const response = await this.client.get(`/projects/${projectId}/production-zones`);
    return response.data;
  }

  async reorderProductionZones(projectId: number, ordered_ids: number[]) {
    const response = await this.client.post(`/projects/${projectId}/production-zones/reorder`, { ordered_ids });
    return response.data;
  }

  async createProductionZone(projectId: number, data: { name: string; parent_zone_id?: number | null; workload_points?: number }) {
    const response = await this.client.post(`/projects/${projectId}/production-zones`, data);
    return response.data;
  }

  async updateProductionZone(projectId: number, zoneId: number, data: { name: string; parent_zone_id?: number | null; sort_order?: number | null; workload_points?: number }) {
    const response = await this.client.put(`/projects/${projectId}/production-zones/${zoneId}`, data);
    return response.data;
  }

  async deleteProductionZone(projectId: number, zoneId: number) {
    const response = await this.client.delete(`/projects/${projectId}/production-zones/${zoneId}`);
    return response.data;
  }

  async addProductionZoneItem(projectId: number, zoneId: number, data: { production_item_id: number; target_quantity: number; unit_override?: string | null; weight?: number | null }) {
    const response = await this.client.post(`/projects/${projectId}/production-zones/${zoneId}/items`, data);
    return response.data;
  }

  async updateProductionZoneItem(projectId: number, zoneId: number, itemId: number, data: { target_quantity: number; unit_override?: string | null; planned_rate_per_manday?: number | null; weight?: number | null }) {
    const response = await this.client.put(`/projects/${projectId}/production-zones/${zoneId}/items/${itemId}`, data);
    return response.data;
  }

  async deleteProductionZoneItem(projectId: number, zoneId: number, itemId: number) {
    const response = await this.client.delete(`/projects/${projectId}/production-zones/${zoneId}/items/${itemId}`);
    return response.data;
  }

  // Workforce planning
  async getWorkforcePlan(projectId: number, manpowerOverride?: number | null) {
    const params: Record<string, any> = { project_id: projectId };
    if (manpowerOverride != null) params.manpower = manpowerOverride;
    const response = await this.client.get('/reports/workforce-plan', { params });
    return response.data;
  }

  async getZoneTimeline(projectId: number, manpowerOverride?: number | null) {
    const params: Record<string, any> = {};
    if (manpowerOverride != null) params.manpower = manpowerOverride;
    const response = await this.client.get(`/projects/${projectId}/zone-timeline`, { params });
    return response.data;
  }

  async getProjectSchedule(projectId: number, options?: { suppressAuthRedirect?: boolean }) {
    const response = await this.client.get(`/projects/${projectId}/schedule`, {
      suppressAuthRedirect: Boolean(options?.suppressAuthRedirect),
    } as any);
    return response.data;
  }

  async updateProjectScheduleAnchor(projectId: number, data: { schedule_start_date: string }, options?: { versionId?: number | null }) {
    const response = await this.client.patch(`/projects/${projectId}/schedule/anchor`, data, {
      params: options?.versionId ? { version_id: options.versionId } : undefined,
    });
    return response.data;
  }

  async getProjectScheduleVarianceSummary(projectId: number) {
    const response = await this.client.get(`/projects/${projectId}/schedule/variance-summary`);
    return response.data;
  }

  async getProjectScheduleRecoveryForecast(projectId: number) {
    const response = await this.client.get(`/projects/${projectId}/schedule/recovery-forecast`);
    return response.data;
  }

  async getProjectScheduleVersions(projectId: number) {
    const response = await this.client.get(`/projects/${projectId}/schedule/versions`);
    return response.data;
  }

  async getProjectScheduleVersion(projectId: number, versionId: number) {
    const response = await this.client.get(`/projects/${projectId}/schedule/versions/${versionId}`);
    return response.data;
  }

  async getProjectScheduleVersionHistory(projectId: number, versionId: number) {
    const response = await this.client.get(`/projects/${projectId}/schedule/versions/${versionId}/history`);
    return response.data;
  }

  async createProjectScheduleBaseline(projectId: number, data?: { title?: string; note?: string | null }) {
    const response = await this.client.post(`/projects/${projectId}/schedule/versions/baseline`, data || {});
    return response.data;
  }

  async createProjectScheduleSnapshot(projectId: number, data?: { title?: string; note?: string | null }) {
    const response = await this.client.post(`/projects/${projectId}/schedule/versions/snapshots`, data || {});
    return response.data;
  }

  async setProjectScheduleVersionLock(projectId: number, versionId: number, data: { locked: boolean; reason: string }) {
    const response = await this.client.patch(`/projects/${projectId}/schedule/versions/${versionId}/lock`, data);
    return response.data;
  }

  async compareProjectSchedules(projectId: number, params?: { left?: string; right?: string }) {
    const response = await this.client.get(`/projects/${projectId}/schedule/compare`, { params });
    return response.data;
  }

  async createProjectScheduleCategory(projectId: number, data: { name: string; color: string }, options?: { versionId?: number | null }) {
    const response = await this.client.post(`/projects/${projectId}/schedule/categories`, data, {
      params: options?.versionId ? { version_id: options.versionId } : undefined,
    });
    return response.data;
  }

  async reorderProjectScheduleCategories(projectId: number, orderedIds: number[], options?: { versionId?: number | null }) {
    const response = await this.client.patch(`/projects/${projectId}/schedule/categories/reorder`, { order: orderedIds }, {
      params: options?.versionId ? { version_id: options.versionId } : undefined,
    });
    return response.data;
  }

  async deleteProjectScheduleCategory(projectId: number, categoryId: number, options?: { versionId?: number | null }) {
    const response = await this.client.delete(`/projects/${projectId}/schedule/categories/${categoryId}`, {
      params: options?.versionId ? { version_id: options.versionId } : undefined,
    });
    return response.data;
  }

  async createProjectScheduleTask(projectId: number, data: { category_id: number; title: string; start_week: number; end_week: number; start_date?: string | null; end_date?: string | null; status: string; priority?: string; section?: string; note?: string }, options?: { versionId?: number | null }) {
    const response = await this.client.post(`/projects/${projectId}/schedule/tasks`, data, {
      params: options?.versionId ? { version_id: options.versionId } : undefined,
    });
    return response.data;
  }

  async reorderProjectScheduleTasks(projectId: number, orderedIds: number[], options?: { versionId?: number | null }) {
    const response = await this.client.patch(`/projects/${projectId}/schedule/tasks/reorder`, { ordered_ids: orderedIds }, {
      params: options?.versionId ? { version_id: options.versionId } : undefined,
    });
    return response.data;
  }

  async updateProjectScheduleTask(projectId: number, taskId: number, data: { category_id?: number; title?: string; start_week?: number; end_week?: number; start_date?: string | null; end_date?: string | null; status?: string; priority?: string; section?: string; note?: string | null; force_unlocked?: boolean; force_unlock_reason?: string; week_mode?: boolean }, options?: { versionId?: number | null }) {
    const response = await this.client.patch(`/projects/${projectId}/schedule/tasks/${taskId}`, data, {
      params: options?.versionId ? { version_id: options.versionId } : undefined,
    });
    return response.data;
  }

  async getProjectWorkCrews(projectId: number): Promise<ProjectWorkCrew[]> {
    const response = await this.client.get(`/projects/${projectId}/work-crews`);
    return response.data;
  }

  async createProjectWorkCrew(projectId: number, data: {
    name: string;
    description?: string | null;
    budget_category_id?: number | null;
    external_cost_id?: number | null;
    change_order_id?: number | null;
  }): Promise<ProjectWorkCrew> {
    const response = await this.client.post(`/projects/${projectId}/work-crews`, data);
    return response.data;
  }

  async updateProjectWorkCrew(projectId: number, crewId: number, data: {
    name?: string;
    description?: string | null;
    status?: 'active' | 'inactive';
  }): Promise<ProjectWorkCrew> {
    const response = await this.client.patch(`/projects/${projectId}/work-crews/${crewId}`, data);
    return response.data;
  }

  async removeProjectWorkCrew(projectId: number, crewId: number) {
    const response = await this.client.delete(`/projects/${projectId}/work-crews/${crewId}`);
    return response.data as { success: boolean; deleted: boolean };
  }

  async updateProjectWorkCrewBudgetSource(projectId: number, crewId: number, data: {
    budget_category_id?: number | null;
    external_cost_id?: number | null;
    change_order_id?: number | null;
  }): Promise<ProjectWorkCrew> {
    const response = await this.client.patch(`/projects/${projectId}/work-crews/${crewId}/budget-source`, data);
    return response.data;
  }

  async previewWorkCrewHistoricalBudgetReclassification(
    projectId: number,
    crewId: number,
    options?: { dateFrom?: string; dateTo?: string },
  ): Promise<WorkCrewHistoricalReclassificationPreview> {
    const response = await this.client.get(
      `/projects/${projectId}/work-crews/${crewId}/historical-budget-reclassification`,
      { params: { date_from: options?.dateFrom || undefined, date_to: options?.dateTo || undefined } },
    );
    return response.data;
  }

  async applyWorkCrewHistoricalBudgetReclassification(
    projectId: number,
    crewId: number,
    data: {
      date_from?: string | null;
      date_to?: string | null;
      include_approved: boolean;
      confirmation: string;
    },
  ): Promise<{ applied_rows: number; total_hours: number; affected_week_starts: string[] }> {
    const response = await this.client.post(
      `/projects/${projectId}/work-crews/${crewId}/historical-budget-reclassification`,
      data,
    );
    return response.data;
  }

  async assignProjectWorkCrewMember(projectId: number, crewId: number, employeeId: number, data?: {
    member_role?: string | null;
    is_lead?: boolean;
    overhead_mode?: 'inherit' | 'custom' | 'none';
    overhead_percentage?: number | null;
  }) {
    const response = await this.client.post(`/projects/${projectId}/work-crews/${crewId}/members`, {
      employee_id: employeeId,
      ...data,
    });
    return response.data as { success: boolean };
  }

  async updateProjectWorkCrewMember(projectId: number, crewId: number, employeeId: number, data: {
    member_role?: string | null;
    is_lead?: boolean;
    overhead_mode?: 'inherit' | 'custom' | 'none';
    overhead_percentage?: number | null;
  }) {
    const response = await this.client.patch(
      `/projects/${projectId}/work-crews/${crewId}/members/${employeeId}`,
      data,
    );
    return response.data;
  }

  async removeProjectWorkCrewMember(projectId: number, crewId: number, employeeId: number) {
    const response = await this.client.delete(`/projects/${projectId}/work-crews/${crewId}/members/${employeeId}`);
    return response.data as { success: boolean };
  }

  async getProjectRentedIdentities(projectId: number): Promise<ProjectRentedIdentityDirectory> {
    const response = await this.client.get(`/projects/${projectId}/rented-identities`);
    return response.data;
  }

  async getPersonNameMatches(name: string, projectId?: number): Promise<{ matches: PersonNameMatch[] }> {
    const response = await this.client.get('/people/name-matches', {
      params: { name, ...(projectId != null ? { project_id: projectId } : {}) },
      // Advisory reads must not navigate away or show global network toasts.
      suppressAuthRedirect: true,
      suppressAuthRefresh: true,
    } as any);
    return response.data;
  }

  async createProjectRentedIdentity(projectId: number, displayName: string) {
    const response = await this.client.post(`/projects/${projectId}/rented-identities`, {
      display_name: displayName,
    });
    return response.data;
  }

  async mergeProjectRentedIdentities(projectId: number, personIds: number[], displayName?: string) {
    const response = await this.client.post(`/projects/${projectId}/rented-identities/merge`, {
      person_ids: personIds,
      display_name: displayName,
    });
    return response.data as { success: boolean; person_id: number };
  }

  async rejectProjectRentedIdentitySuggestion(projectId: number, personAId: number, personBId: number) {
    const response = await this.client.post(`/projects/${projectId}/rented-identities/reject-suggestion`, {
      person_a_id: personAId,
      person_b_id: personBId,
    });
    return response.data as { success: boolean };
  }

  async linkProjectRentedProfile(projectId: number, personId: number, employeeId: number) {
    const response = await this.client.post(
      `/projects/${projectId}/rented-identities/${personId}/profiles`,
      { employee_id: employeeId },
    );
    return response.data as { success: boolean };
  }

  async unlinkProjectRentedProfile(projectId: number, personId: number, employeeId: number) {
    const response = await this.client.delete(
      `/projects/${projectId}/rented-identities/${personId}/profiles/${employeeId}`,
    );
    return response.data as { success: boolean; person_id: number };
  }

  async updateProjectScheduleTasksBatch(
    projectId: number,
    updates: Array<{ task_id: number; category_id?: number; start_week?: number; end_week?: number; start_date?: string | null; end_date?: string | null; week_mode?: boolean }>,
    options?: { versionId?: number | null },
  ) {
    const response = await this.client.patch(`/projects/${projectId}/schedule/tasks/batch`, { updates }, {
      params: options?.versionId ? { version_id: options.versionId } : undefined,
    });
    return response.data;
  }

  async deleteProjectScheduleTask(projectId: number, taskId: number, options?: { versionId?: number | null }) {
    const response = await this.client.delete(`/projects/${projectId}/schedule/tasks/${taskId}`, {
      params: options?.versionId ? { version_id: options.versionId } : undefined,
    });
    return response.data;
  }

  async createProjectScheduleTaskScopeLink(projectId: number, data: { task_id: number; scope_item_id: number; target_share_percent?: number; override_quantity?: number | null; note?: string | null }) {
    const response = await this.client.post(`/projects/${projectId}/schedule/task-scope-links`, data);
    return response.data;
  }

  async bulkCreateProjectScheduleTaskScopeLinks(projectId: number, data: { task_id: number; scope_item_ids: number[] }) {
    const response = await this.client.post(`/projects/${projectId}/schedule/task-scope-links/bulk`, data);
    return response.data;
  }

  async updateProjectScheduleTaskScopeLink(projectId: number, linkId: number, data: { target_share_percent?: number; override_quantity?: number | null; note?: string | null }) {
    const response = await this.client.patch(`/projects/${projectId}/schedule/task-scope-links/${linkId}`, data);
    return response.data;
  }

  async deleteProjectScheduleTaskScopeLink(projectId: number, linkId: number) {
    const response = await this.client.delete(`/projects/${projectId}/schedule/task-scope-links/${linkId}`);
    return response.data;
  }

  async updateProjectScheduleTaskWorkers(projectId: number, taskId: number, data: { week_number: number; worker_count: number }, options?: { versionId?: number | null }) {
    const response = await this.client.patch(`/projects/${projectId}/schedule/tasks/${taskId}/workers`, data, {
      params: options?.versionId ? { version_id: options.versionId } : undefined,
    });
    return response.data;
  }

  async createProjectScheduleDependency(projectId: number, data: { predecessor_task_id: number; successor_task_id: number }, options?: { versionId?: number | null }) {
    const response = await this.client.post(`/projects/${projectId}/schedule/dependencies`, data, {
      params: options?.versionId ? { version_id: options.versionId } : undefined,
    });
    return response.data;
  }

  async deleteProjectScheduleDependency(
    projectId: number,
    dependencyId: number,
    data?: { predecessor_task_id?: number; successor_task_id?: number },
    options?: { versionId?: number | null },
  ) {
    const response = await this.client.delete(`/projects/${projectId}/schedule/dependencies/${dependencyId}`, {
      data,
      params: options?.versionId ? { version_id: options.versionId } : undefined,
    });
    return response.data;
  }

  async exportEmployeeHoursToExcel(params?: { week_start?: string; week_end?: string; project_id?:number; employment_type?: 'company' | 'rented' }) {
    const response = await this.client.get('/employees/hours-report/excel', {
      params,
      responseType: 'blob',
    });
    return response.data;
  }

  async createEmployee(name: string, hourly_rate: number, position?: string, hire_date?: string, employment_type?: string, contractor_company?: string, nameParts?: { first_name: string; last_name: string }) {
    return this.peopleApi.createEmployee(name, hourly_rate, position, hire_date, employment_type, contractor_company, nameParts);
  }

  async updateEmployee(id: number, data: { first_name?: string; last_name?: string;
    name?: string;
    hourly_rate?: number;
    position?: string;
    hire_date?: string;
    employment_type?: string;
    contractor_company?: string | null;
    annual_pto_days?: number;
    rate_effective_date?: string;
    rate_note?: string;
    profile_photo?: string | null;
  }) {
    return this.peopleApi.updateEmployee(id, data);
  }

  async getEmployeeRateHistory(id: number) {
    return this.peopleApi.getEmployeeRateHistory(id);
  }

  async deleteEmployeeRateHistoryEntry(id: number, historyId: number) {
    return this.peopleApi.deleteEmployeeRateHistoryEntry(id, historyId);
  }

  async editEmployeeRateHistoryEntry(id: number, historyId: number, data: { hourly_rate?: number; effective_date?: string; note?: string }) {
    return this.peopleApi.editEmployeeRateHistoryEntry(id, historyId, data);
  }

  async getEmployeeEmergencyContacts(id: number) {
    return this.peopleApi.getEmployeeEmergencyContacts(id);
  }

  async createEmployeeEmergencyContact(id: number, data: {
    name: string;
    relationship?: string;
    phone?: string;
    email?: string;
    is_primary?: boolean;
    notes?: string;
  }) {
    return this.peopleApi.createEmployeeEmergencyContact(id, data);
  }

  async updateEmployeeEmergencyContact(id: number, contactId: number, data: {
    name?: string;
    relationship?: string | null;
    phone?: string | null;
    email?: string | null;
    is_primary?: boolean;
    notes?: string | null;
  }) {
    return this.peopleApi.updateEmployeeEmergencyContact(id, contactId, data);
  }

  async deleteEmployeeEmergencyContact(id: number, contactId: number) {
    return this.peopleApi.deleteEmployeeEmergencyContact(id, contactId);
  }

  async scheduleEmployeeOffboarding(id: number, data: {
    separation_type: string;
    separation_reason?: string;
    last_day_worked?: string;
    separation_effective_date: string;
    rehire_eligible?: boolean;
    separation_notes?: string;
  }) {
    return this.peopleApi.scheduleEmployeeOffboarding(id, data);
  }

  async cancelEmployeeOffboarding(id: number) {
    return this.peopleApi.cancelEmployeeOffboarding(id);
  }

  async finalizeEmployeeOffboarding(id: number, data?: {
    separation_type?: string;
    separation_reason?: string;
    last_day_worked?: string;
    rehire_eligible?: boolean;
    separation_notes?: string;
  }) {
    return this.peopleApi.finalizeEmployeeOffboarding(id, data);
  }

  async getCredentialTypes(includeInactive?: boolean) {
    return this.peopleApi.getCredentialTypes(includeInactive);
  }

  async createCredentialType(data: { name: string; category?: string; requires_issue_date?: boolean; requires_expiration?: boolean; description?: string; importance?: import('../types').CredentialImportance }) {
    return this.peopleApi.createCredentialType(data);
  }

  async updateCredentialType(id: number, data: { name?: string; category?: string; requires_issue_date?: boolean; requires_expiration?: boolean; description?: string; is_active?: boolean; importance?: import('../types').CredentialImportance }) {
    return this.peopleApi.updateCredentialType(id, data);
  }

  async deleteCredentialType(id: number) {
    return this.peopleApi.deleteCredentialType(id);
  }

  async getCredentialsDashboard() {
    return this.peopleApi.getCredentialsDashboard();
  }

  async getEmployeeCredentials(id: number) {
    return this.peopleApi.getEmployeeCredentials(id);
  }

  async getEmployeeCredentialAttachment(id: number, credentialId: number) {
    return this.peopleApi.getEmployeeCredentialAttachment(id, credentialId);
  }

  async createEmployeeCredential(id: number, data: {
    credential_type_id: number;
    completion_date?: string;
    expiration_date?: string;
    no_expiration?: boolean;
    card_number?: string;
    issuer?: string;
    attachment_data?: string;
    attachment_filename?: string;
    notes?: string;
  }) {
    return this.peopleApi.createEmployeeCredential(id, data);
  }

  async updateEmployeeCredential(id: number, credentialId: number, data: {
    completion_date?: string | null;
    expiration_date?: string | null;
    no_expiration?: boolean;
    card_number?: string | null;
    issuer?: string | null;
    attachment_data?: string | null;
    attachment_filename?: string | null;
    notes?: string | null;
    verified?: boolean;
  }) {
    return this.peopleApi.updateEmployeeCredential(id, credentialId, data);
  }

  async deleteEmployeeCredential(id: number, credentialId: number) {
    return this.peopleApi.deleteEmployeeCredential(id, credentialId);
  }

  async getEmployeeDocuments(id: number) {
    return this.peopleApi.getEmployeeDocuments(id);
  }

  async getEmployeeDocumentAttachment(id: number, documentId: number) {
    return this.peopleApi.getEmployeeDocumentAttachment(id, documentId);
  }

  async createEmployeeDocument(id: number, data: {
    document_type: import('../types').EmployeeDocumentType;
    label?: string;
    document_date?: string;
    expiration_date?: string;
    attachment_data: string;
    attachment_filename?: string;
    notes?: string;
  }) {
    return this.peopleApi.createEmployeeDocument(id, data);
  }

  async updateEmployeeDocument(id: number, documentId: number, data: {
    label?: string | null;
    document_date?: string | null;
    expiration_date?: string | null;
    attachment_data?: string | null;
    attachment_filename?: string | null;
    notes?: string | null;
  }) {
    return this.peopleApi.updateEmployeeDocument(id, documentId, data);
  }

  async deleteEmployeeDocument(id: number, documentId: number) {
    return this.peopleApi.deleteEmployeeDocument(id, documentId);
  }

  async deleteEmployee(id: number) {
    return this.peopleApi.deleteEmployee(id);
  }

  async toggleEmployeeActive(id: number) {
    return this.peopleApi.toggleEmployeeActive(id);
  }

  // Admin alerts
  async getAdminAlerts(options?: { suppressAuthRedirect?: boolean }) { return this.dashboardApi.getAdminAlerts(options); }

  // Admin dashboard stats (this week vs last week)
  async getDashboardStats(options?: { suppressAuthRedirect?: boolean }) { return this.dashboardApi.getDashboardStats(options); }
  async getRfiAttentionSummary(options?: { suppressAuthRedirect?: boolean }) { return this.dashboardApi.getRfiAttentionSummary(options); }

  async getSecurityCenter(queryString?: string) { return this.securityApi.getSecurityCenter(queryString); }

  async getSecurityAlerts() { return this.securityApi.getSecurityAlerts(); }

  async getSecurityAlertDetail(id: number) { return this.securityApi.getSecurityAlertDetail(id); }

  async reviewSecurityAlert(id: number, data?: { status?: string; review_note?: string }) { return this.securityApi.reviewSecurityAlert(id, data); }

  async revokeSecuritySession(id: number) { return this.securityApi.revokeSecuritySession(id); }

  async revokeAllUserSessions(userId: number) { return this.securityApi.revokeAllUserSessions(userId); }

  async suspendUserFromSecurityCenter(userId: number) { return this.securityApi.suspendUserFromSecurityCenter(userId); }

  async forcePasswordResetFromSecurityCenter(userId: number) { return this.securityApi.forcePasswordResetFromSecurityCenter(userId); }
  async unlockAccountFromSecurityCenter(userId: number) { return this.securityApi.unlockAccountFromSecurityCenter(userId); }

  async getAuditEvents(queryString?: string) { return this.securityApi.getAuditEvents(queryString); }

  async getAuditEventDetail(eventId: string) { return this.securityApi.getAuditEventDetail(eventId); }
  async recordAuditDataAction(input: {
    action: 'download' | 'export' | 'print';
    module: string;
    resource_type: string;
    resource_id: string | number;
    project_id?: number | null;
  }) {
    await this.client.post('/audit/client-data-action', input, { suppressAuthRedirect: true } as any);
  }

  // Export endpoints
  async exportToExcel(weekStart: string, weekEnd?: string) {
    const params: any = { week: weekStart, week_start: weekStart };
    if (weekEnd) params.week_end = weekEnd;
    const response = await this.client.get('/export/excel', {
      params,
      responseType: 'blob',
    });
    return response.data;
  }

  // Company settings
  async getSettings() { return this.settingsApi.getSettings(); }
  async listAnnouncements() { return this.settingsApi.listAnnouncements(); }
  async uploadAnnouncementImage(data: string) { return this.settingsApi.uploadAnnouncementImage(data); }
  async getAnnouncementImage(imageId: number, announcementId?: number) { return this.settingsApi.getAnnouncementImage(imageId, announcementId); }
  async acknowledgeAnnouncement(id: number) { return this.settingsApi.acknowledgeAnnouncement(id); }
  async listManagedAnnouncements() { return this.settingsApi.listManagedAnnouncements(); }
  async saveAnnouncement(data: import('../types').AnnouncementInput, id?: number) { return this.settingsApi.saveAnnouncement(data, id); }
  async withdrawAnnouncement(id: number) { return this.settingsApi.withdrawAnnouncement(id); }
  async getLeaveCostPolicy() { return (await this.client.get('/settings/leave-cost-policy')).data; }
  async previewLeaveCostCorrection(filters: {project_id:number|null;start_date:string|null;end_date:string|null}) {
    return (await this.client.get('/settings/leave-cost-reclassification',{params:filters})).data as import('../types').LeaveCostCorrectionPreview;
  }
  async applyLeaveCostCorrection(data: {filters:import('../types').LeaveCostCorrectionPreview['filters'];preview_hash:string;request_key:string;reason:string;confirmation:'MOVE_TO_COMPANY'}) {
    return (await this.client.post('/settings/leave-cost-reclassification',data)).data;
  }
  async saveLeaveCostPolicy(policy: { sick: string; pto: string; holiday: string; training?: string; effective_date: string }) {
    return (await this.client.put('/settings/leave-cost-policy',policy)).data;
  }
  async getFieldReportSettings() { return this.settingsApi.getFieldReportSettings(); }
  async getBranding() { return this.settingsApi.getBranding(); }
  async updateSettings(data: Record<string, any>) { return this.settingsApi.updateSettings(data); }
  async getDropboxIntegrationStatus() { return this.settingsApi.getDropboxIntegrationStatus(); }
  async startDropboxConnect() { return this.settingsApi.startDropboxConnect(); }
  async disconnectDropbox() { return this.settingsApi.disconnectDropbox(); }
  async testDropboxUpload() { return this.settingsApi.testDropboxUpload(); }
  async getReceiptAiIntegrationStatus() { return this.settingsApi.getReceiptAiIntegrationStatus(); }
  async connectReceiptAi(apiKey: string) { return this.settingsApi.connectReceiptAi(apiKey); }
  async disconnectReceiptAi() { return this.settingsApi.disconnectReceiptAi(); }
  async updateReceiptAiSettings(data: Parameters<SettingsApi['updateReceiptAiSettings']>[0]) { return this.settingsApi.updateReceiptAiSettings(data); }
  async testReceiptAiConnection() { return this.settingsApi.testReceiptAiConnection(); }
  async getReceiptAiMemories(filters?: Parameters<SettingsApi['getReceiptAiMemories']>[0]) { return this.settingsApi.getReceiptAiMemories(filters); }
  async getReceiptAiMemoryMetrics(days?: number) { return this.settingsApi.getReceiptAiMemoryMetrics(days); }
  async getReceiptAiMemoryHistory(id: number) { return this.settingsApi.getReceiptAiMemoryHistory(id); }
  async updateReceiptAiMemory(id: number, data: Parameters<SettingsApi['updateReceiptAiMemory']>[1]) { return this.settingsApi.updateReceiptAiMemory(id, data); }

  // Templates
  async getTemplates() { return this.settingsApi.getTemplates(); }
  async createTemplate(name: string, rows: any[], include_hours = false) { return this.settingsApi.createTemplate(name, rows, include_hours); }
  async deleteTemplate(id: number) { return this.settingsApi.deleteTemplate(id); }
  // Supervisor dashboard
  async getSupervisorDashboard(weekStart?: string) { return this.dashboardApi.getSupervisorDashboard(weekStart); }
  async setSupervisorHiddenEmployee(employeeId: number, hidden: boolean) { return this.dashboardApi.setSupervisorHiddenEmployee(employeeId, hidden); }

  // Maintenance
  async getMaintenanceStatus() { return this.settingsApi.getMaintenanceStatus(); }
  async getManualMaintenanceStatus() { return this.settingsApi.getManualMaintenanceStatus(); }
  async setManualMaintenanceMode(active: boolean) { return this.settingsApi.setManualMaintenanceMode(active); }
  async listMaintenanceWindows() { return this.settingsApi.listMaintenanceWindows(); }
  async createMaintenanceWindow(data: Partial<MaintenanceWindow>) { return this.settingsApi.createMaintenanceWindow(data); }
  async updateMaintenanceWindow(id: number, data: Partial<MaintenanceWindow>) { return this.settingsApi.updateMaintenanceWindow(id, data); }
  async deleteMaintenanceWindow(id: number) { return this.settingsApi.deleteMaintenanceWindow(id); }
  async getTimesheetFinancialRebuildPreview(projectId?: number) { return this.settingsApi.getTimesheetFinancialRebuildPreview(projectId); }
  async rebuildTimesheetFinancials(data: { project_id?: number | null; week_start_dates: string[]; confirmation: 'REBUILD' }) { return this.settingsApi.rebuildTimesheetFinancials(data); }

  // Authorization engine rewrite (developer / security tooling)
  async getAuthzEngineMode() { return this.authzApi.getEngineMode(); }
  async setAuthzEngineMode(mode: Parameters<AuthzApi['setEngineMode']>[0], reason: string) { return this.authzApi.setEngineMode(mode, reason); }
  async getAuthzShadowReport(engine?: Parameters<AuthzApi['getShadowReport']>[0], limit?: number) { return this.authzApi.getShadowReport(engine, limit); }
  async getAuthzCatalog() { return this.authzApi.getCatalog(); }
  async listAuthzAssignments() { return this.authzApi.listAssignments(); }
  async setAuthzAssignment(userId: number, templateKey: string) { return this.authzApi.setAssignment(userId, templateKey); }
  async explainAuthz(userId: number, query: Parameters<AuthzApi['explain']>[1]) { return this.authzApi.explain(userId, query); }
  // ── Weekly production reports ─────────────────────────────────────────────
  async getWeeklyReports(projectId: number) {
    const response = await this.client.get(`/projects/${projectId}/weekly-reports`);
    return response.data as { reports: any[]; last_crew_size: number | null };
  }

  async getWeeklyReport(projectId: number, weekStart: string) {
    const response = await this.client.get(`/projects/${projectId}/weekly-reports/${weekStart}`);
    return response.data as { report: any | null; entries: any[] };
  }

  async upsertWeeklyReport(projectId: number, weekStart: string, data: {
    crew_size?: number | null;
    notes?: string;
    entries: Array<{ zone_id: number; production_item_id: number; quantity_installed: number; notes?: string }>;
  }) {
    const response = await this.client.put(`/projects/${projectId}/weekly-reports/${weekStart}`, { week_start: weekStart, ...data });
    return response.data as { report: any; crew_changed: boolean };
  }

  async submitWeeklyReport(projectId: number, reportId: number) {
    const response = await this.client.post(`/projects/${projectId}/weekly-reports/${reportId}/submit`, {});
    return response.data;
  }

  // ── Field Reports (new CRUD) ──────────────────────────────────────────────
  async getFieldReports(projectId: number) {
    const response = await this.client.get(`/projects/${projectId}/field-reports`);
    return response.data as { reports: any[] };
  }

  async getFieldReport(projectId: number, reportId: number) {
    const response = await this.client.get(`/projects/${projectId}/field-reports/${reportId}`);
    return response.data as { report: any; entries: any[]; photos?: any[] };
  }

  async createFieldReport(projectId: number, data: {
    period_type: 'daily' | 'weekly';
    report_date: string;
    crew_size?: number | null;
    supervisor_id?: number | null;
    notes?: string | null;
  }) {
    const response = await this.client.post(`/projects/${projectId}/field-reports`, data);
    return response.data as { report: any };
  }

  async updateFieldReport(projectId: number, reportId: number, data: {
    period_type?: 'daily' | 'weekly';
    report_date?: string;
    crew_size?: number | null;
    supervisor_id?: number | null;
    notes?: string | null;
    status?: 'draft' | 'submitted' | 'approved';
  }) {
    const response = await this.client.put(`/projects/${projectId}/field-reports/${reportId}`, data);
    return response.data as { report: any };
  }

  async deleteFieldReport(projectId: number, reportId: number) {
    const response = await this.client.delete(`/projects/${projectId}/field-reports/${reportId}`);
    return response.data as { ok: boolean };
  }

  async addFieldReportEntry(projectId: number, reportId: number, data: {
    zone_id: number;
    production_item_id?: number | null;
    quantity_installed?: number;
    worker_names?: string | null;
    hours_worked?: number | null;
    work_description?: string | null;
    notes?: string | null;
  }) {
    const response = await this.client.post(`/projects/${projectId}/field-reports/${reportId}/entries`, data);
    return response.data as { entry: any };
  }

  /** All-or-nothing: the backend validates every row before inserting any. */
  async addFieldReportEntriesBulk(projectId: number, reportId: number, entries: Array<{
    zone_id: number;
    production_item_id: number;
    quantity_installed?: number;
    worker_names?: string | null;
    hours_worked?: number | null;
    work_description?: string | null;
    notes?: string | null;
  }>) {
    const response = await this.client.post(`/projects/${projectId}/field-reports/${reportId}/entries/bulk`, { entries });
    return response.data as { entries: any[] };
  }

  async updateFieldReportEntry(projectId: number, reportId: number, entryId: number, data: Partial<{
    zone_id: number;
    production_item_id: number | null;
    quantity_installed: number;
    worker_names: string | null;
    hours_worked: number | null;
    work_description: string | null;
    notes: string | null;
  }>) {
    const response = await this.client.put(`/projects/${projectId}/field-reports/${reportId}/entries/${entryId}`, data);
    return response.data as { entry: any };
  }

  async deleteFieldReportEntry(projectId: number, reportId: number, entryId: number) {
    const response = await this.client.delete(`/projects/${projectId}/field-reports/${reportId}/entries/${entryId}`);
    return response.data as { ok: boolean };
  }

  async uploadFieldReportPhoto(projectId: number, reportId: number, data: {
    file_name: string;
    content_type: string;
    data_base64: string;
    entry_id?: number | null;
  }) {
    const response = await this.client.post(`/projects/${projectId}/field-reports/${reportId}/photos`, data, { timeout: 60000 });
    return response.data as { photo: any };
  }

  async uploadFieldReportAttachment(
    projectId: number,
    reportId: number,
    file: File,
    contentType: string,
    entryId: number | null = null,
    onProgress?: (percent: number) => void,
    zone?: string | null,
  ) {
    const sessionResponse = await this.client.post(
      `/projects/${projectId}/field-reports/${reportId}/photos/upload-session`,
      {
        file_name: file.name,
        content_type: contentType,
        file_size: file.size,
        entry_id: entryId,
        zone,
      },
      { timeout: 60000 },
    );
    const uploadToken = String(sessionResponse.data?.upload_token || '');
    const chunkSize = Number(sessionResponse.data?.chunk_size || 0);
    if (!uploadToken || !Number.isSafeInteger(chunkSize) || chunkSize <= 0) {
      throw new Error('The attachment upload session could not be started');
    }

    let offset = 0;
    while (offset < file.size) {
      const end = Math.min(file.size, offset + chunkSize);
      const isFinal = end === file.size;
      const chunk = await file.slice(offset, end).arrayBuffer();
      const chunkResponse = await this.client.post(
        `/projects/${projectId}/field-reports/${reportId}/photos/upload-session/chunk`,
        chunk,
        {
          timeout: 90000,
          headers: {
            'Content-Type': 'application/octet-stream',
            'X-Field-Upload-Token': uploadToken,
            'X-Upload-Offset': String(offset),
            'X-Upload-Final': isFinal ? '1' : '0',
          },
          transformRequest: (data) => data,
        },
      );

      offset = end;
      onProgress?.(Math.round((offset / file.size) * 100));
      if (isFinal) return chunkResponse.data as { photo: any };

      const acknowledgedOffset = Number(chunkResponse.data?.next_offset);
      if (acknowledgedOffset !== offset) {
        throw new Error('The attachment upload was interrupted');
      }
    }

    throw new Error('The attachment was empty');
  }

  getFieldReportPhotoUrl(projectId: number, reportId: number, photoId: number) {
    return `${API_BASE_URL}/projects/${projectId}/field-reports/${reportId}/photos/${photoId}/view`;
  }

  getFieldReportPhotoThumbnailUrl(projectId: number, reportId: number, photoId: number) {
    return `${API_BASE_URL}/projects/${projectId}/field-reports/${reportId}/photos/${photoId}/thumbnail`;
  }

  getWarehouseItemPhotoThumbnailUrl(itemId: number, photoId: number) {
    return `${API_BASE_URL}/warehouse/items/${itemId}/photos/${photoId}/thumbnail`;
  }

  async getFieldReportPdfPreview(
    projectId: number,
    reportId: number,
    photoId: number,
    signal?: AbortSignal,
    onProgress?: (percent: number) => void,
  ) {
    const response = await this.client.get(
      `/projects/${projectId}/field-reports/${reportId}/photos/${photoId}/pdf-preview`,
      {
        responseType: 'arraybuffer',
        timeout: 90000,
        signal,
        onDownloadProgress: (event) => {
          if (!event.total || event.total <= 0) return;
          onProgress?.(Math.min(100, Math.round((event.loaded / event.total) * 100)));
        },
      },
    );
    return response.data as ArrayBuffer;
  }

  async deleteFieldReportPhoto(projectId: number, reportId: number, photoId: number) {
    const response = await this.client.delete(`/projects/${projectId}/field-reports/${reportId}/photos/${photoId}`);
    return response.data as { ok: boolean };
  }

  async createFieldReportPhotoPin(projectId: number, reportId: number, photoId: number, data: {
    page_number?: number;
    x_ratio: number;
    y_ratio: number;
    comment: string;
  }) {
    const response = await this.client.post(`/projects/${projectId}/field-reports/${reportId}/photos/${photoId}/pins`, data);
    return response.data as { pin: any };
  }

  async deleteFieldReportPhotoPin(projectId: number, reportId: number, photoId: number, pinId: number) {
    const response = await this.client.delete(`/projects/${projectId}/field-reports/${reportId}/photos/${photoId}/pins/${pinId}`);
    return response.data as { ok: boolean };
  }

  async getFieldReportSupervisors(projectId: number) {
    const response = await this.client.get(`/projects/${projectId}/field-reports/supervisors`);
    return response.data as { supervisors: Array<{ id: number; name: string; email: string }> };
  }

  // ── Variaciones + control ─────────────────────────────────────────────────
  async getVariaciones(projectId: number) {
    const response = await this.client.get(`/projects/${projectId}/variaciones`);
    return response.data as { variaciones: any[]; standalone_snapshots: any[] };
  }

  async createVariacion(projectId: number, data: {
    type: 'crew_change' | 'schedule_change' | 'scope_change' | 'manual';
    description?: string;
    week_start?: string;
    old_value?: any;
    new_value?: any;
  }) {
    const response = await this.client.post(`/projects/${projectId}/variaciones`, data);
    return response.data;
  }

  async createProductionSnapshot(projectId: number, data: {
    snapshot_name?: string;
    is_baseline?: boolean;
    variacion_id?: number | null;
  }) {
    const response = await this.client.post(`/projects/${projectId}/production-snapshots`, data);
    return response.data;
  }

  async getProductionBaseline(projectId: number) {
    const response = await this.client.get(`/projects/${projectId}/production-baseline`);
    return response.data as { baseline: any | null };
  }

  async getPlanActual(projectId: number) {
    const response = await this.client.get(`/projects/${projectId}/plan-actual`);
    return response.data as {
      zones: any[];
      current_crew: number | null;
      overall_progress_percent: number;
      total_target: number;
      total_installed: number;
    };
  }

  // ── Warehouse ──────────────────────────────────────────────────────────────
  async getWarehouseRequestProjects() { return this.warehouseApi.getWarehouseRequestProjects(); }
  async getPendingRequestPurchases() { return this.warehouseApi.getPendingRequestPurchases(); }
  async linkRequestPurchase(deliveryLineId: number, purchaseLineId: number) { return this.warehouseApi.linkRequestPurchase(deliveryLineId, purchaseLineId); }
  async getWarehouseOverview(projectId?: number | null) { return this.warehouseApi.getWarehouseOverview(projectId); }
  async getWarehouseItems(params?: { category?: string; item_type?: string; search?: string; project_id?: number; missing_photo?: boolean }) { return this.warehouseApi.getWarehouseItems(params); }
  async createWarehouseItem(data: any) { return this.warehouseApi.createWarehouseItem(data); }
  async resolveWarehouseRequestLineCatalog(...args: Parameters<WarehouseApi['resolveWarehouseRequestLineCatalog']>) { return this.warehouseApi.resolveWarehouseRequestLineCatalog(...args); }
  async createWarehouseProductOptions(data: import('../types').WarehouseProductOptionsInput) { return this.warehouseApi.createWarehouseProductOptions(data); }
  async updateWarehouseItem(id: number, data: any) { return this.warehouseApi.updateWarehouseItem(id, data); }
  async deleteWarehouseItem(id: number) { return this.warehouseApi.deleteWarehouseItem(id); }
  async adjustWarehouseStock(id: number, quantity: number, note: string, expectedQuantity?: number) { return this.warehouseApi.adjustWarehouseStock(id, quantity, note, expectedQuantity); }
  async getWarehouseItemHistory(id: number) { return this.warehouseApi.getWarehouseItemHistory(id); }
  async getWarehouseRequests(params?: { project_id?: number; status?: string; view?: 'mine' | 'queue'; q?: string; priority?: string }) { return this.warehouseApi.getWarehouseRequests(params); }
  async getWarehouseRequestSettings() { return this.warehouseApi.getWarehouseRequestSettings(); }
  async updateWarehouseRequestSettings(qrEnabled: boolean) { return this.warehouseApi.updateWarehouseRequestSettings(qrEnabled); }
  async getWarehouseRequestCatalog(params?: { q?: string; category?: string; item_type?: string; limit?: number; include_photos?: boolean; family_id?: number; selection?: string; ungrouped?: boolean }) { return this.warehouseApi.getWarehouseRequestCatalog(params); }
  async getWarehouseFamilies(requestsOnly = false) { return this.warehouseApi.getWarehouseFamilies(requestsOnly); }
  async saveWarehouseFamily(data: Omit<import('../types').WarehouseItemFamily, 'id'>, id?: number) { return this.warehouseApi.saveWarehouseFamily(data, id); }
  async saveWarehouseVariant(familyId: number, data: { attributes: Record<string, string>; item_id?: number; sku?: string }) { return this.warehouseApi.saveWarehouseVariant(familyId, data); }
  async getWarehouseFamilyOptions(familyId: number, selection: Record<string, string>) { return this.warehouseApi.getWarehouseFamilyOptions(familyId, selection); }
  async getWarehouseRequestMetrics() { return this.warehouseApi.getWarehouseRequestMetrics(); }
  async getWarehouseRequest(id: number) { return this.warehouseApi.getWarehouseRequest(id); }
  async createWarehouseRequest(data: any) { return this.warehouseApi.createWarehouseRequest(data); }
  async cancelWarehouseRequest(id: number, reason?: string) { return this.warehouseApi.cancelWarehouseRequest(id, reason); }
  async uploadWarehouseRequestLinePhoto(id: number, lineId: number, data: { file_name: string; content_type: string; file_size: number; file_base64: string }) { return this.warehouseApi.uploadWarehouseRequestLinePhoto(id, lineId, data); }
  async startWarehouseRequestPreparation(id: number) { return this.warehouseApi.startWarehouseRequestPreparation(id); }
  async setWarehouseRequestPreparedQuantity(id: number, attemptId: number, lineId: number, data: { quantity: number; fulfilled_item_id?: number; substitution_reason?: string }) { return this.warehouseApi.setWarehouseRequestPreparedQuantity(id, attemptId, lineId, data); }
  async closeWarehouseRequestNotSupplied(id: number, attemptId: number, lineId: number, reason: string, quantity?: number) { return this.warehouseApi.closeWarehouseRequestNotSupplied(id, attemptId, lineId, reason, quantity); }
  async scanWarehouseRequestUnit(id: number, attemptId: number, lineId: number, data: Parameters<WarehouseApi['scanWarehouseRequestUnit']>[3]) { return this.warehouseApi.scanWarehouseRequestUnit(id, attemptId, lineId, data); }
  async removeWarehouseRequestPreparedUnit(id: number, attemptId: number, fulfillmentId: number) { return this.warehouseApi.removeWarehouseRequestPreparedUnit(id, attemptId, fulfillmentId); }
  async dispatchWarehouseRequest(id: number, attemptId: number) { return this.warehouseApi.dispatchWarehouseRequest(id, attemptId); }
  async uploadWarehouseRequestDeliveryLinePhoto(id: number, attemptId: number, lineId: number, data: { file_name: string; content_type: string; file_size: number; file_base64: string }) { return this.warehouseApi.uploadWarehouseRequestDeliveryLinePhoto(id, attemptId, lineId, data); }
  async recordWarehouseRequestDelivery(id: number, attemptId: number) { return this.warehouseApi.recordWarehouseRequestDelivery(id, attemptId); }
  async confirmWarehouseRequestDeliveryLine(id: number, attemptId: number, lineId: number, data?: { quantity: number; unit_ids?: number[] }) { return this.warehouseApi.confirmWarehouseRequestDeliveryLine(id, attemptId, lineId, data); }
  async disputeWarehouseRequestDeliveryLine(id: number, attemptId: number, lineId: number, reason: string) { return this.warehouseApi.disputeWarehouseRequestDeliveryLine(id, attemptId, lineId, reason); }
  async resolveWarehouseRequestDispute(id: number, attemptId: number, reason: string) { return this.warehouseApi.resolveWarehouseRequestDispute(id, attemptId, reason); }
  async setWarehouseRequestFulfillmentSource(id: number, attemptId: number, lineId: number, fulfillmentSource: string) { return this.warehouseApi.setWarehouseRequestFulfillmentSource(id, attemptId, lineId, fulfillmentSource); }
  async getWarehouseRequestAvailableUnits(id: number, lineId: number, q?: string) { return this.warehouseApi.getWarehouseRequestAvailableUnits(id, lineId, q); }
  async getWarehouseRequestComments(id: number) { return this.warehouseApi.getWarehouseRequestComments(id); }
  async addWarehouseRequestComment(id: number, body: string, requestLineId?: number | null) { return this.warehouseApi.addWarehouseRequestComment(id, body, requestLineId); }
  async getWarehouseRequestEvidenceUrl(id: number, evidenceId: number) { return this.warehouseApi.getWarehouseRequestEvidenceUrl(id, evidenceId); }
  async updateWarehouseRequest(id: number, action: string, extra?: any) { return this.warehouseApi.updateWarehouseRequest(id, action, extra); }
  async deleteWarehouseRequest(id: number) { return this.warehouseApi.deleteWarehouseRequest(id); }
  async uploadWarehouseRequestPhoto(id: number, data: { file_name: string; content_type: string; file_size: number; file_base64: string }) { return this.warehouseApi.uploadWarehouseRequestPhoto(id, data); }
  async getWarehouseRequestPhotos(id: number) { return this.warehouseApi.getWarehouseRequestPhotos(id); }
  async getWarehouseRequestPhotoUrl(id: number, photoId: number) { return this.warehouseApi.getWarehouseRequestPhotoUrl(id, photoId); }
  async linkWarehouseRequestItem(id: number, itemId: number) { return this.warehouseApi.linkWarehouseRequestItem(id, itemId); }
  async getWarehouseInventoryCorrections(params?: { project_id?: number; status?: string }) { return this.warehouseApi.getWarehouseInventoryCorrections(params); }
  async createWarehouseInventoryCorrection(data: any) { return this.warehouseApi.createWarehouseInventoryCorrection(data); }
  async getWarehouseInventoryCorrection(id: number) { return this.warehouseApi.getWarehouseInventoryCorrection(id); }
  async reviewWarehouseInventoryCorrection(id: number, action: 'start_review' | 'needs_verification' | 'approve' | 'reject', reason?: string) { return this.warehouseApi.reviewWarehouseInventoryCorrection(id, action, reason); }
  async applyWarehouseInventoryCorrection(id: number, reason?: string, applicationAction?: string, decision?: Parameters<WarehouseApi['applyWarehouseInventoryCorrection']>[3]) { return this.warehouseApi.applyWarehouseInventoryCorrection(id, reason, applicationAction, decision); }
  async getWarehouseInventoryCorrectionCandidates(id: number) { return this.warehouseApi.getWarehouseInventoryCorrectionCandidates(id); }
  async getWarehouseRequestProjectTools(projectId: number) { return this.warehouseApi.getWarehouseRequestProjectTools(projectId); }
  async reverseWarehouseInventoryCorrection(id: number, reason: string) { return this.warehouseApi.reverseWarehouseInventoryCorrection(id, reason); }
  async uploadWarehouseInventoryCorrectionEvidence(id: number, data: { file_name: string; content_type: string; file_size: number; file_base64: string }) { return this.warehouseApi.uploadWarehouseInventoryCorrectionEvidence(id, data); }
  async getWarehouseInventoryCorrectionEvidenceUrl(id: number, evidenceId: number) { return this.warehouseApi.getWarehouseInventoryCorrectionEvidenceUrl(id, evidenceId); }
  async getWarehouseDeliveries(projectId?: number | null, status?: string) { return this.warehouseApi.getWarehouseDeliveries(projectId, status); }
  async getWarehouseDelivery(id: number) { return this.warehouseApi.getWarehouseDelivery(id); }
  async createWarehouseDelivery(data: any) { return this.warehouseApi.createWarehouseDelivery(data); }
  async confirmWarehouseDelivery(id: number, data: any) { return this.warehouseApi.confirmWarehouseDelivery(id, data); }
  async getWarehouseLogs(projectId?: number | null) { return this.warehouseApi.getWarehouseLogs(projectId); }
  async createWarehouseLog(data: any) { return this.warehouseApi.createWarehouseLog(data); }
  async getWarehouseMovements(filters?: { project_id?: number; item_id?: number; type?: string }) { return this.warehouseApi.getWarehouseMovements(filters); }
  async getWarehouseUnifiedLogs(filters?: {
    log_group?: string; log_type?: string; item_id?: number; project_id?: number;
    date_from?: string; date_to?: string; status?: string; created_by?: number;
  }) { return this.warehouseApi.getWarehouseUnifiedLogs(filters); }
  async softDeleteWarehouseLog(source: string, id: number, reason?: string) { return this.warehouseApi.softDeleteWarehouseLog(source, id, reason); }
  async getWarehouseItemLots(itemId: number) { return this.warehouseApi.getWarehouseItemLots(itemId); }
  async addWarehouseStockLot(itemId: number, data: any) { return this.warehouseApi.addWarehouseStockLot(itemId, data); }
  async updateWarehouseStockLotCost(itemId: number, lotId: number, data: any) { return this.warehouseApi.updateWarehouseStockLotCost(itemId, lotId, data); }
  async sendWarehouseStockLotToProject(itemId: number, lotId: number, data: { project_id: number; quantity: number; note?: string }) { return this.warehouseApi.sendWarehouseStockLotToProject(itemId, lotId, data); }
  async getWarehouseItemUnits(itemId: number) { return this.warehouseApi.getWarehouseItemUnits(itemId); }
  async getWarehouseUnitScanInfo(unitId: number, source?: Parameters<WarehouseApi['getWarehouseUnitScanInfo']>[1]) { return this.warehouseApi.getWarehouseUnitScanInfo(unitId, source); }
  async getWarehouseRequestUnitScanInfo(unitId: number, source?: Parameters<WarehouseApi['getWarehouseRequestUnitScanInfo']>[1]) { return this.warehouseApi.getWarehouseRequestUnitScanInfo(unitId, source); }
  async resolveWarehouseUnit(q: string, itemId?: number) { return this.warehouseApi.resolveWarehouseUnit(q, itemId); }
  async getWarehouseUnitIdentity(itemId: number, unitId: number) { return this.warehouseApi.getWarehouseUnitIdentity(itemId, unitId); }
  async addWarehouseUnitIdentifier(itemId: number, unitId: number, data: Parameters<WarehouseApi['addWarehouseUnitIdentifier']>[2]) { return this.warehouseApi.addWarehouseUnitIdentifier(itemId, unitId, data); }
  async removeWarehouseUnitIdentifier(itemId: number, unitId: number, identifierId: number) { return this.warehouseApi.removeWarehouseUnitIdentifier(itemId, unitId, identifierId); }
  async issueWarehouseUnitTag(itemId: number, unitId: number) { return this.warehouseApi.issueWarehouseUnitTag(itemId, unitId); }
  async replaceWarehouseUnitTag(itemId: number, unitId: number, reason: Parameters<WarehouseApi['replaceWarehouseUnitTag']>[2]) { return this.warehouseApi.replaceWarehouseUnitTag(itemId, unitId, reason); }
  async getWarehouseTag(token: string) { return this.warehouseApi.getWarehouseTag(token); }
  async saveWarehouseUnitSerials(itemId: number, units: Parameters<WarehouseApi['saveWarehouseUnitSerials']>[1]) { return this.warehouseApi.saveWarehouseUnitSerials(itemId, units); }
  async createWarehouseItemUnit(itemId: number, data: any) { return this.warehouseApi.createWarehouseItemUnit(itemId, data); }
  async updateWarehouseItemUnit(itemId: number, unitId: number, data: any) { return this.warehouseApi.updateWarehouseItemUnit(itemId, unitId, data); }
  async deleteWarehouseItemUnit(itemId: number, unitId: number) { return this.warehouseApi.deleteWarehouseItemUnit(itemId, unitId); }
  async checkoutWarehouseUnit(itemId: number, unitId: number, data: any) { return this.warehouseApi.checkoutWarehouseUnit(itemId, unitId, data); }
  async checkinWarehouseUnit(itemId: number, unitId: number, data: any) { return this.warehouseApi.checkinWarehouseUnit(itemId, unitId, data); }
  async moveWarehouseUnitDirect(itemId: number, unitId: number, data: { to_project_id?: number; to_location_id?: number; to_location_text?: string; notes?: string }) { return this.warehouseApi.moveWarehouseUnitDirect(itemId, unitId, data); }
  async createWarehouseTransfer(data: { unit_ids: number[]; to_project_id?: number; to_location_id?: number; to_location_text?: string; notes?: string }) { return this.warehouseApi.createWarehouseTransfer(data); }
  async receiveWarehouseTransfer(id: number) { return this.warehouseApi.receiveWarehouseTransfer(id); }
  async cancelWarehouseTransfer(id: number) { return this.warehouseApi.cancelWarehouseTransfer(id); }
  async getWarehouseTransfers(params?: { status?: string }) { return this.warehouseApi.getWarehouseTransfers(params); }
  async getWarehouseTransfer(id: number) { return this.warehouseApi.getWarehouseTransfer(id); }
  async getWarehouseProjectCloseoutCheck(projectId: number) { return this.warehouseApi.getWarehouseProjectCloseoutCheck(projectId); }
  async getWarehouseUnitHistory(itemId: number, unitId: number) { return this.warehouseApi.getWarehouseUnitHistory(itemId, unitId); }
  async getWarehouseItemLogIdentifiers(itemId: number): Promise<string[]> { return this.warehouseApi.getWarehouseItemLogIdentifiers(itemId); }
  async uploadWarehouseItemPhoto(itemId: number, data: { file_name: string; content_type: string; file_size: number; file_base64: string }) { return this.warehouseApi.uploadWarehouseItemPhoto(itemId, data); }
  async getWarehouseItemPhotos(itemId: number) { return this.warehouseApi.getWarehouseItemPhotos(itemId); }
  async setPrimaryWarehouseItemPhoto(itemId: number, photoId: number) { return this.warehouseApi.setPrimaryWarehouseItemPhoto(itemId, photoId); }
  async deleteWarehouseItemPhoto(itemId: number, photoId: number) { return this.warehouseApi.deleteWarehouseItemPhoto(itemId, photoId); }
  async getWarehouseItemPhotoUrl(itemId: number, photoId: number) { return this.warehouseApi.getWarehouseItemPhotoUrl(itemId, photoId); }
  async getWarehouseItemAliases(itemId: number) { return this.warehouseApi.getWarehouseItemAliases(itemId); }
  async addWarehouseItemAlias(itemId: number, alias: string) { return this.warehouseApi.addWarehouseItemAlias(itemId, alias); }
  async deleteWarehouseItemAlias(itemId: number, aliasId: number) { return this.warehouseApi.deleteWarehouseItemAlias(itemId, aliasId); }
  async getWarehouseLocations(params?: { include_archived?: boolean }) { return this.warehouseApi.getWarehouseLocations(params); }
  async createWarehouseLocation(data: { name: string; type: string; notes?: string; project_id?: number }) { return this.warehouseApi.createWarehouseLocation(data); }
  async updateWarehouseLocation(id: number, data: any) { return this.warehouseApi.updateWarehouseLocation(id, data); }
  async archiveWarehouseLocation(id: number) { return this.warehouseApi.archiveWarehouseLocation(id); }
  async getWarehouseLocationInventory(id: number) { return this.warehouseApi.getWarehouseLocationInventory(id); }
  async getWarehouseVendors() { return this.warehouseApi.getWarehouseVendors(); }
  async createWarehouseVendor(data: any) { return this.warehouseApi.createWarehouseVendor(data); }
  async updateWarehouseVendor(id: number, data: any) { return this.warehouseApi.updateWarehouseVendor(id, data); }
  async deleteWarehouseVendor(id: number) { return this.warehouseApi.deleteWarehouseVendor(id); }
  async getWarehouseProjectInventory(projectId: number) { return this.warehouseApi.getWarehouseProjectInventory(projectId); }
  async exportWarehouseProjectInventory(projectId?: number | null): Promise<Blob> { return this.warehouseApi.exportWarehouseProjectInventory(projectId); }
  async exportWarehouseStock(): Promise<Blob> { return this.warehouseApi.exportWarehouseStock(); }
  async exportWarehouseFullReport(): Promise<Blob> { return this.warehouseApi.exportWarehouseFullReport(); }

  // Warehouse Purchases
  async getWarehouseAnalysis(params: import('../types').WarehouseAnalysisFilters) { return this.warehouseApi.getWarehouseAnalysis(params); }
  async getWarehousePurchases(params?: { status?: string; vendor_id?: number; date_from?: string; date_to?: string }) { return this.warehouseApi.getWarehousePurchases(params); }
  async getWarehousePurchaseSummary(params?: { date_from?: string; date_to?: string; category?: string; project_id?: number | 'none'; location_id?: number | 'none' }) { return this.warehouseApi.getWarehousePurchaseSummary(params); }
  async getWarehousePurchase(id: number) { return this.warehouseApi.getWarehousePurchase(id); }
  async createWarehousePurchase(data: any) { return this.warehouseApi.createWarehousePurchase(data); }
  async updateWarehousePurchase(id: number, data: any) { return this.warehouseApi.updateWarehousePurchase(id, data); }
  async submitWarehousePurchase(id: number) { return this.warehouseApi.submitWarehousePurchase(id); }
  async recallWarehousePurchase(id: number) { return this.warehouseApi.recallWarehousePurchase(id); }
  async returnWarehousePurchaseForCorrection(id: number, reason: string) { return this.warehouseApi.returnWarehousePurchaseForCorrection(id, reason); }
  async postWarehousePurchase(id: number) { return this.warehouseApi.postWarehousePurchase(id); }
  async reopenPostedWarehousePurchase(id: number, reason: string) { return this.warehouseApi.reopenPostedWarehousePurchase(id, reason); }
  async voidWarehousePurchase(id: number, void_reason: string) { return this.warehouseApi.voidWarehousePurchase(id, void_reason); }
  async deleteWarehousePurchase(id: number) { return this.warehouseApi.deleteWarehousePurchase(id); }
  async uploadWarehousePurchaseReceipt(id: number, data: { file_name: string; content_type: string; file_size: number; file_base64: string }) { return this.warehouseApi.uploadWarehousePurchaseReceipt(id, data); }
  async getWarehousePurchaseReceiptUrl(id: number, photoId: number) { return this.warehouseApi.getWarehousePurchaseReceiptUrl(id, photoId); }
  async deleteWarehousePurchaseReceiptPhoto(id: number, photoId: number) { return this.warehouseApi.deleteWarehousePurchaseReceiptPhoto(id, photoId); }
  async scanWarehousePurchaseReceipt(data: Parameters<WarehouseApi['scanWarehousePurchaseReceipt']>[0]) { return this.warehouseApi.scanWarehousePurchaseReceipt(data); }
  async scanExistingWarehousePurchaseReceipt(id: number, data: Parameters<WarehouseApi['scanExistingWarehousePurchaseReceipt']>[1]) { return this.warehouseApi.scanExistingWarehousePurchaseReceipt(id, data); }
  async requestWarehousePurchaseReceiptException(id: number, reason: string) { return this.warehouseApi.requestWarehousePurchaseReceiptException(id, reason); }
  async reviewWarehousePurchaseReceiptException(id: number, action: 'approve' | 'reject', reason?: string) { return this.warehouseApi.reviewWarehousePurchaseReceiptException(id, action, reason); }
  async linkVendorToPurchase(id: number, vendor_id: number) { return this.warehouseApi.linkVendorToPurchase(id, vendor_id); }
  async createVendorFromReceipt(id: number, data: { name: string; vendor_type?: 'vendor' | 'store' }) { return this.warehouseApi.createVendorFromReceipt(id, data); }
  async bulkMoveUnitsToLocation(data: { unit_ids: number[]; location_id?: number; location_text?: string }) { return this.warehouseApi.bulkMoveUnitsToLocation(data); }
  async getForecasts() { return this.forecastApi.getForecasts(); }
  async getForecast(id: number) { return this.forecastApi.getForecast(id); }
  async createForecast(data: any) { return this.forecastApi.createForecast(data); }
  async updateForecast(id: number, data: any) { return this.forecastApi.updateForecast(id, data); }
  async deleteForecast(id: number) { return this.forecastApi.deleteForecast(id); }
  async createForecastSnapshot(id: number, data: any) { return this.forecastApi.createForecastSnapshot(id, data); }

  async getProjectRelatedItems(
    projectId: number,
    entityType: import('../types').ProjectCoordinationEntityType,
    entityId: number,
  ) {
    return this.projectCoordinationApi.getRelatedItems(projectId, entityType, entityId);
  }
  async searchProjectRelatedItemCandidates(
    projectId: number,
    input: {
      source_entity_type: import('../types').ProjectCoordinationEntityType;
      source_entity_id: number;
      q: string;
      entity_type?: import('../types').ProjectCoordinationEntityType;
    },
  ) {
    return this.projectCoordinationApi.searchRelatedItemCandidates(projectId, input);
  }
  async createProjectRelatedItem(
    projectId: number,
    input: import('../types').CreateProjectRelatedItemInput,
  ) {
    return this.projectCoordinationApi.createRelatedItem(projectId, input);
  }
  async deleteProjectRelatedItem(projectId: number, relationId: number) {
    return this.projectCoordinationApi.deleteRelatedItem(projectId, relationId);
  }
  async getProjectDocumentCategories(projectId: number) {
    return this.projectCoordinationApi.getDocumentCategories(projectId);
  }
  async updateProjectDocumentCategory(
    projectId: number,
    categoryId: number,
    input: { is_enabled: boolean; sort_order: number },
  ) {
    return this.projectCoordinationApi.updateDocumentCategory(projectId, categoryId, input);
  }
  async reorderProjectDocumentCategories(projectId: number, categoryIds: number[]) {
    return this.projectCoordinationApi.reorderDocumentCategories(projectId, categoryIds);
  }
  async getProjectDocumentFolders(projectId: number, options?: { include_archived?: boolean }) {
    return this.projectCoordinationApi.getDocumentFolders(projectId, options);
  }
  async createProjectDocumentFolder(projectId: number, name: string) {
    return this.projectCoordinationApi.createDocumentFolder(projectId, name);
  }
  async updateProjectDocumentFolder(projectId: number, folderId: number, input: { name?: string; is_archived?: boolean }) {
    return this.projectCoordinationApi.updateDocumentFolder(projectId, folderId, input);
  }
  async getProjectDocuments(
    projectId: number,
    filters?: { status?: 'active' | 'archived' | 'all'; category_id?: number; folder_id?: number | 'none'; search?: string },
  ) {
    return this.projectCoordinationApi.getDocuments(projectId, filters);
  }
  async getProjectDocument(projectId: number, documentId: number) {
    return this.projectCoordinationApi.getDocument(projectId, documentId);
  }
  async getProjectDocumentHistory(projectId: number, documentId: number) {
    return this.projectCoordinationApi.getDocumentHistory(projectId, documentId);
  }
  async startProjectDocumentUpload(
    projectId: number,
    input: import('../types').ProjectDocumentUploadInput,
  ) {
    return this.projectCoordinationApi.startDocumentUpload(projectId, input);
  }
  async startProjectDocumentVersionUpload(
    projectId: number,
    documentId: number,
    input: import('../types').ProjectDocumentVersionUploadInput,
  ) {
    return this.projectCoordinationApi.startDocumentVersionUpload(projectId, documentId, input);
  }
  async uploadProjectDocumentChunk(
    projectId: number,
    token: string,
    offset: number,
    chunk: Blob,
    isFinal: boolean,
  ) {
    return this.projectCoordinationApi.uploadDocumentChunk(projectId, token, offset, chunk, isFinal);
  }
  async updateProjectDocument(
    projectId: number,
    documentId: number,
    input: Partial<Pick<import('../types').ProjectDocument, 'category_id' | 'title' | 'description' | 'tags' | 'content_classification'>> & { folder_id?: number },
  ) {
    return this.projectCoordinationApi.updateDocument(projectId, documentId, input);
  }
  async setProjectDocumentArchived(projectId: number, documentId: number, archived: boolean) {
    return this.projectCoordinationApi.setDocumentArchived(projectId, documentId, archived);
  }
  async openProjectDocumentVersion(projectId: number, documentId: number, versionId: number) {
    return this.projectCoordinationApi.openDocumentVersion(projectId, documentId, versionId);
  }
  async getProjectDrawingSets(
    projectId: number,
    filters?: { status?: 'active' | 'archived' | 'all'; search?: string },
  ) {
    return this.projectCoordinationApi.getDrawingSets(projectId, filters);
  }
  async getProjectDrawingSet(projectId: number, setId: number) {
    return this.projectCoordinationApi.getDrawingSet(projectId, setId);
  }
  async openProjectDrawingSet(projectId: number, setId: number) {
    return this.projectCoordinationApi.openDrawingSet(projectId, setId);
  }
  async getProjectDrawingSetDelivery(projectId: number, setId: number, options?: { signal?: AbortSignal }) {
    return this.projectCoordinationApi.getDrawingSetDelivery(projectId, setId, options);
  }
  async getProjectDrawingSetHistory(projectId: number, setId: number) {
    return this.projectCoordinationApi.getDrawingSetHistory(projectId, setId);
  }
  async startProjectDrawingSetUpload(
    projectId: number,
    input: import('../types').ProjectDrawingSetUploadInput,
  ) {
    return this.projectCoordinationApi.startDrawingSetUpload(projectId, input);
  }
  async uploadProjectDrawingSetChunk(
    projectId: number,
    token: string,
    offset: number,
    chunk: Blob,
    isFinal: boolean,
  ) {
    return this.projectCoordinationApi.uploadDrawingSetChunk(projectId, token, offset, chunk, isFinal);
  }
  async setProjectDrawingSetArchived(projectId: number, setId: number, archived: boolean) {
    return this.projectCoordinationApi.setDrawingSetArchived(projectId, setId, archived);
  }

  async publishProjectDrawingSetSource(projectId: number, setId: number) {
    return this.projectCoordinationApi.publishDrawingSetSource(projectId, setId);
  }
  async deleteProjectDrawingSetPermanently(projectId: number, setId: number) {
    return this.projectCoordinationApi.deleteDrawingSetPermanently(projectId, setId);
  }
  async setProjectDrawingSetPageSkipped(
    projectId: number,
    setId: number,
    pageNumber: number,
    skipped: boolean,
  ) {
    return this.projectCoordinationApi.setDrawingSetPageSkipped(projectId, setId, pageNumber, skipped);
  }
  async classifyProjectDrawingSetPage(
    projectId: number,
    setId: number,
    pageNumber: number,
    input: import('../types').ProjectDrawingClassificationInput,
  ) {
    return this.projectCoordinationApi.classifyDrawingSetPage(projectId, setId, pageNumber, input);
  }
  async reviewProjectDrawingSetPageMatch(projectId: number, setId: number, pageNumber: number, decision: 'confirm' | 'reject') {
    return this.projectCoordinationApi.reviewDrawingSetPageMatch(projectId, setId, pageNumber, decision);
  }
  async recognizeProjectDrawingSetPage(projectId: number, setId: number, pageNumber: number, image: Blob, signal?: AbortSignal) {
    return this.projectCoordinationApi.recognizeDrawingSetPage(projectId, setId, pageNumber, image, signal);
  }
  async batchProjectDrawingSetPages(projectId: number, setId: number, input: Parameters<ProjectCoordinationApi['batchDrawingSetPages']>[2]) {
    return this.projectCoordinationApi.batchDrawingSetPages(projectId, setId, input);
  }
  async getProjectDrawings(
    projectId: number,
    filters?: { status?: 'active' | 'archived' | 'all'; discipline?: string; search?: string; drawing_set_id?: number },
    options?: { signal?: AbortSignal },
  ) {
    return this.projectCoordinationApi.getDrawings(projectId, filters, options);
  }
  async getProjectDrawing(projectId: number, drawingId: number) {
    return this.projectCoordinationApi.getDrawing(projectId, drawingId);
  }
  async getProjectDrawingHistory(projectId: number, drawingId: number) {
    return this.projectCoordinationApi.getDrawingHistory(projectId, drawingId);
  }
  async getProjectDrawingViewer(projectId: number, drawingId: number, revisionId?: number, options?: { signal?: AbortSignal }) {
    return this.projectCoordinationApi.getDrawingViewerBootstrap(projectId, drawingId, revisionId, options);
  }

  async getProjectDrawingRevisionDelivery(
    projectId: number,
    drawingId: number,
    revisionId: number,
    preferDerived = true,
    options?: { signal?: AbortSignal },
  ) {
    return this.projectCoordinationApi.getDrawingRevisionDelivery(
      projectId,
      drawingId,
      revisionId,
      preferDerived,
      options,
    );
  }
  async getProjectDrawingOfflineManifest(projectId: number, drawingId: number, revisionId: number) {
    return this.projectCoordinationApi.getDrawingOfflineManifest(projectId, drawingId, revisionId);
  }
  async listProjectDrawingOfflineManifest(projectId: number, drawingSetId?: number) {
    return this.projectCoordinationApi.listDrawingOfflineManifest(projectId, drawingSetId);
  }
  async getProjectDrawingOfflineDelivery(
    projectId: number,
    drawingId: number,
    revisionId: number,
    options?: { signal?: AbortSignal },
  ) {
    return this.projectCoordinationApi.getDrawingOfflineDelivery(projectId, drawingId, revisionId, options);
  }
  async getProjectDrawingRevisionThumbnail(
    projectId: number,
    drawingId: number,
    revisionId: number,
    options?: { signal?: AbortSignal },
  ) {
    return this.projectCoordinationApi.getDrawingRevisionThumbnail(projectId, drawingId, revisionId, options);
  }
  async optimizeProjectDrawingRevision(projectId: number, drawingId: number, revisionId: number) {
    return this.projectCoordinationApi.optimizeDrawingRevision(projectId, drawingId, revisionId);
  }
  async uploadProjectDrawingRevisionThumbnail(projectId: number, drawingId: number, revisionId: number, thumbnail: Blob) {
    return this.projectCoordinationApi.uploadDrawingRevisionThumbnail(projectId, drawingId, revisionId, thumbnail);
  }
  async setProjectDrawingArchived(projectId: number, drawingId: number, archived: boolean) {
    return this.projectCoordinationApi.setDrawingArchived(projectId, drawingId, archived);
  }
  async deleteProjectDrawingPermanently(projectId: number, drawingId: number) {
    return this.projectCoordinationApi.deleteDrawingPermanently(projectId, drawingId);
  }
  async publishProjectDrawingRevision(projectId: number, drawingId: number, revisionId: number) {
    return this.projectCoordinationApi.publishDrawingRevision(projectId, drawingId, revisionId);
  }
  async openProjectDrawingRevision(
    projectId: number,
    drawingId: number,
    revisionId: number,
    preferDerived?: boolean,
  ) {
    return this.projectCoordinationApi.openDrawingRevision(
      projectId,
      drawingId,
      revisionId,
      preferDerived,
    );
  }
  async getProjectDrawingAnnotations(projectId: number, revisionId: number) {
    return this.projectCoordinationApi.getDrawingAnnotations(projectId, revisionId);
  }
  async createProjectDrawingAnnotation(
    projectId: number,
    revisionId: number,
    input: import('../types').ProjectDrawingAnnotationInput,
  ) {
    return this.projectCoordinationApi.createDrawingAnnotation(projectId, revisionId, input);
  }
  async updateProjectDrawingAnnotation(
    projectId: number,
    revisionId: number,
    annotationId: number,
    input: Partial<import('../types').ProjectDrawingAnnotationInput>,
  ) {
    return this.projectCoordinationApi.updateDrawingAnnotation(projectId, revisionId, annotationId, input);
  }
  async deleteProjectDrawingAnnotation(projectId: number, revisionId: number, annotationId: number) {
    return this.projectCoordinationApi.deleteDrawingAnnotation(projectId, revisionId, annotationId);
  }
  async setProjectDrawingAnnotationStatus(projectId: number, revisionId: number, annotationId: number, status: 'open' | 'resolved') { return this.projectCoordinationApi.setDrawingAnnotationStatus(projectId, revisionId, annotationId, status); }
  async reviewProjectDrawingAnnotation(projectId: number, revisionId: number, annotationId: number, decision: import('../types').ProjectDrawingAnnotationReviewDecision, point?: { x_ratio: number; y_ratio: number }) { return this.projectCoordinationApi.reviewDrawingAnnotation(projectId, revisionId, annotationId, decision, point); }
  async getProjectDrawingAnnotationThread(projectId: number, revisionId: number, annotationId: number) { return this.projectCoordinationApi.getDrawingAnnotationThread(projectId, revisionId, annotationId); }
  async createProjectDrawingAnnotationMessage(projectId: number, revisionId: number, annotationId: number, body: string) { return this.projectCoordinationApi.createDrawingAnnotationMessage(projectId, revisionId, annotationId, body); }
  async startProjectDrawingAnnotationAttachmentUpload(projectId: number, revisionId: number, annotationId: number, input: { file_name: string; content_type: string; file_size: number }) { return this.projectCoordinationApi.startDrawingAnnotationAttachmentUpload(projectId, revisionId, annotationId, input); }
  async uploadProjectDrawingAnnotationAttachmentChunk(projectId: number, revisionId: number, annotationId: number, token: string, offset: number, chunk: Blob, isFinal: boolean) { return this.projectCoordinationApi.uploadDrawingAnnotationAttachmentChunk(projectId, revisionId, annotationId, token, offset, chunk, isFinal); }
  async openProjectDrawingAnnotationAttachment(projectId: number, revisionId: number, annotationId: number, attachmentId: number) { return this.projectCoordinationApi.openDrawingAnnotationAttachment(projectId, revisionId, annotationId, attachmentId); }
  async deleteProjectDrawingAnnotationAttachment(projectId: number, revisionId: number, annotationId: number, attachmentId: number) { return this.projectCoordinationApi.deleteDrawingAnnotationAttachment(projectId, revisionId, annotationId, attachmentId); }
  // Complete PDF pages use a set/page target, independently of sheet revisions.
  async getDrawingSetPageAnnotations(projectId: number, setId: number, page: number, signal?: AbortSignal): Promise<import('../types').DrawingSetPageAnnotationState> {
    return (await this.client.get(`/projects/${projectId}/drawing-sets/${setId}/pages/${page}/annotations`, { signal })).data;
  }
  async createDrawingSetPagePin(projectId: number, setId: number, page: number, input: Pick<import('../types').ProjectDrawingAnnotationInput, 'x_ratio' | 'y_ratio' | 'comment'> & { client_request_id?: string }): Promise<import('../types').ProjectDrawingAnnotation> {
    return (await this.client.post(`/projects/${projectId}/drawing-sets/${setId}/pages/${page}/annotations`, input)).data.annotation;
  }
  async changeDrawingSetPagePin(projectId: number, setId: number, page: number, id: number, input: { status?: 'open' | 'resolved'; x_ratio?: number; y_ratio?: number }): Promise<import('../types').ProjectDrawingAnnotation> {
    return (await this.client.patch(`/projects/${projectId}/drawing-sets/${setId}/pages/${page}/annotations/${id}`, input)).data.annotation;
  }
  async deleteDrawingSetPagePin(projectId: number, setId: number, page: number, id: number): Promise<void> {
    await this.client.delete(`/projects/${projectId}/drawing-sets/${setId}/pages/${page}/annotations/${id}`);
  }
  async getDrawingSetPagePinThread(projectId: number, setId: number, page: number, id: number): Promise<import('../types').ProjectDrawingAnnotationThread> {
    return (await this.client.get(`/projects/${projectId}/drawing-sets/${setId}/pages/${page}/annotations/${id}/messages`)).data;
  }
  async replyDrawingSetPagePin(projectId: number, setId: number, page: number, id: number, body: string): Promise<import('../types').ProjectDrawingAnnotationMessage> {
    return (await this.client.post(`/projects/${projectId}/drawing-sets/${setId}/pages/${page}/annotations/${id}/messages`, { body })).data.message;
  }
  async createDrawingSetPageMarkup(projectId: number, setId: number, page: number, input: import('../types').ProjectDrawingMarkupInput): Promise<import('../types').ProjectDrawingMarkup> {
    return (await this.client.post(`/projects/${projectId}/drawing-sets/${setId}/pages/${page}/markups`, input)).data.markup;
  }
  async updateDrawingSetPageMarkup(projectId: number, setId: number, page: number, id: number, input: Partial<import('../types').ProjectDrawingMarkupInput> & { expected_updated_at: string }): Promise<import('../types').ProjectDrawingMarkup> {
    return (await this.client.patch(`/projects/${projectId}/drawing-sets/${setId}/pages/${page}/markups/${id}`, input)).data.markup;
  }
  async deleteDrawingSetPageMarkup(projectId: number, setId: number, page: number, id: number, expectedUpdatedAt: string): Promise<void> {
    await this.client.delete(`/projects/${projectId}/drawing-sets/${setId}/pages/${page}/markups/${id}`, { data: { expected_updated_at: expectedUpdatedAt } });
  }
  async getProjectDrawingMarkups(projectId: number, revisionId: number) { return this.projectCoordinationApi.getDrawingMarkups(projectId, revisionId); }
  async createProjectDrawingMarkup(projectId: number, revisionId: number, input: import('../types').ProjectDrawingMarkupInput) { return this.projectCoordinationApi.createDrawingMarkup(projectId, revisionId, input); }
  async updateProjectDrawingMarkup(projectId: number, revisionId: number, markupId: number, input: Partial<Pick<import('../types').ProjectDrawingMarkupInput, 'geometry' | 'style' | 'annotation_id' | 'visibility'>> & { expected_updated_at: string }) { return this.projectCoordinationApi.updateDrawingMarkup(projectId, revisionId, markupId, input); }
  async deleteProjectDrawingMarkup(projectId: number, revisionId: number, markupId: number, expectedUpdatedAt: string) { return this.projectCoordinationApi.deleteDrawingMarkup(projectId, revisionId, markupId, expectedUpdatedAt); }
  async postDrawingViewerTelemetry(events: unknown[]) { return this.projectCoordinationApi.postDrawingViewerTelemetry(events); }
  async getProjectRfis(
    projectId: number,
    filters?: { status?: import('../types').ProjectRfiStatus | 'all'; assignee_id?: number; search?: string },
  ) {
    return this.projectCoordinationApi.getRfis(projectId, filters);
  }
  async getProjectRfi(projectId: number, rfiId: number) {
    return this.projectCoordinationApi.getRfi(projectId, rfiId);
  }
  async getProjectRfiAssignableAdmins(projectId: number) {
    return this.projectCoordinationApi.getRfiAssignableAdmins(projectId);
  }
  async createProjectRfiDraft(projectId: number, input: import('../types').ProjectRfiDraftInput) {
    return this.projectCoordinationApi.createRfiDraft(projectId, input);
  }
  async updateProjectRfiDraft(
    projectId: number,
    rfiId: number,
    input: Partial<import('../types').ProjectRfiDraftInput>,
  ) {
    return this.projectCoordinationApi.updateRfiDraft(projectId, rfiId, input);
  }
  async updateProjectRfiManagement(
    projectId: number,
    rfiId: number,
    input: import('../types').ProjectRfiManagementInput,
  ) {
    return this.projectCoordinationApi.updateRfiManagement(projectId, rfiId, input);
  }
  async openProjectRfi(projectId: number, rfiId: number) {
    return this.projectCoordinationApi.openRfi(projectId, rfiId);
  }
  async answerProjectRfi(projectId: number, rfiId: number, response: string) {
    return this.projectCoordinationApi.answerRfi(projectId, rfiId, response);
  }
  async closeProjectRfi(projectId: number, rfiId: number) {
    return this.projectCoordinationApi.closeRfi(projectId, rfiId);
  }
  async reopenProjectRfi(projectId: number, rfiId: number) {
    return this.projectCoordinationApi.reopenRfi(projectId, rfiId);
  }
  async voidProjectRfi(projectId: number, rfiId: number, reason: string) {
    return this.projectCoordinationApi.voidRfi(projectId, rfiId, reason);
  }
  async deleteProjectRfiDraft(projectId: number, rfiId: number) {
    return this.projectCoordinationApi.deleteRfiDraft(projectId, rfiId);
  }
  async startProjectRfiAttachmentUpload(
    projectId: number,
    rfiId: number,
    input: {
      attachment_role: import('../types').ProjectRfiAttachmentRole;
      content_classification?: 'operational' | 'financial';
      file_name: string;
      content_type: string;
      file_size: number;
    },
  ) {
    return this.projectCoordinationApi.startRfiAttachmentUpload(projectId, rfiId, input);
  }
  async uploadProjectRfiAttachmentChunk(
    projectId: number,
    rfiId: number,
    token: string,
    offset: number,
    chunk: Blob,
    isFinal: boolean,
  ) {
    return this.projectCoordinationApi.uploadRfiAttachmentChunk(projectId, rfiId, token, offset, chunk, isFinal);
  }
  async openProjectRfiAttachment(projectId: number, rfiId: number, attachmentId: number) {
    return this.projectCoordinationApi.openRfiAttachment(projectId, rfiId, attachmentId);
  }
  async deleteProjectRfiAttachment(
    projectId: number,
    rfiId: number,
    attachmentId: number,
    role: import('../types').ProjectRfiAttachmentRole,
  ) {
    return this.projectCoordinationApi.deleteRfiAttachment(projectId, rfiId, attachmentId, role);
  }

  async getHolidays(year?: number) { return this.holidayApi.getHolidays(year); }
  async createHoliday(holiday_date: string, name?: string, paid_hours = 8, eligibility_mode: import('../types').CompanyHoliday['eligibility_mode'] = 'review') { return this.holidayApi.createHoliday(holiday_date, name, paid_hours, eligibility_mode); }
  async deleteHoliday(id: number) { return this.holidayApi.deleteHoliday(id); }
  async getHolidayPayReview(id: number) { return this.holidayApi.getHolidayPayReview(id); }
  async saveHolidayPayReview(id: number, entries: Array<{ employee_id: number; decision: string; paid_hours: number; reason?: string }>) { return this.holidayApi.saveHolidayPayReview(id, entries); }
  async finalizeHolidayPayReview(id: number) { return this.holidayApi.finalizeHolidayPayReview(id); }
  async reopenHolidayPayReview(id: number) { return this.holidayApi.reopenHolidayPayReview(id); }
  async getPtoSchedules(employeeId: number) { return this.ptoScheduleApi.getPtoSchedules(employeeId); }
  async createPtoSchedule(params: { employee_id: number; project_id: number; start_date: string; end_date: string; hours_per_day?: number; note?: string }) { return this.ptoScheduleApi.createPtoSchedule(params); }
  async cancelPtoSchedule(id: number) { return this.ptoScheduleApi.cancelPtoSchedule(id); }
  async getProjectOverviewSnapshots(projectId: number) { return this.projectOverviewSnapshotApi.getProjectOverviewSnapshots(projectId); }
  async getProjectOverviewSnapshot(projectId: number, snapshotId: number) { return this.projectOverviewSnapshotApi.getProjectOverviewSnapshot(projectId, snapshotId); }
  async createProjectOverviewSnapshot(projectId: number) { return this.projectOverviewSnapshotApi.createProjectOverviewSnapshot(projectId); }
  async createAllProjectOverviewSnapshots() { return this.projectOverviewSnapshotApi.createAllProjectOverviewSnapshots(); }
  private get personalHoursApi() { return createPersonalTimesheetApi(this.client); }
  getPersonalAccountLink(id:number){return this.personalHoursApi.getPersonalAccountLink(id);}
  listPersonalTimesheets(review=false,week?:string,forUser?:number){return this.personalHoursApi.listPersonalTimesheets(review,week,forUser);}
  getPersonalTimesheet(id:number){return this.personalHoursApi.getPersonalTimesheet(id);}
  savePersonalTimesheet(input:Partial<import("../types").PersonalTimesheet>){return this.personalHoursApi.savePersonalTimesheet(input);}
   transitionPersonalTimesheet(id:number,action:string,reason?:string,expectedUpdatedAt?:string){return this.personalHoursApi.transitionPersonalTimesheet(id,action,reason,expectedUpdatedAt);}
  getPersonalHoursPolicy(){return this.personalHoursApi.getPersonalHoursPolicy();}
  savePersonalHoursPolicy(input:{mode:string;effective_date:string;suggestions:string[]}){return this.personalHoursApi.savePersonalHoursPolicy(input);}
  getPersonalEmployeeInfo(id:number){return this.personalHoursApi.getPersonalEmployeeInfo(id);}
  linkPersonalEmployee(id:number,employee_id:number|null){return this.personalHoursApi.linkPersonalEmployee(id,employee_id);}
  savePersonalCompensation(id:number,pay:import("../types").PersonalPay & {effective_date:string}){return this.personalHoursApi.savePersonalCompensation(id,pay);}
  savePersonalUserCost(id:number,target:string,date:string){return this.personalHoursApi.savePersonalUserCost(id,target,date);}
  getPersonalTimesheetOverlaps(id:number){return this.personalHoursApi.getPersonalTimesheetOverlaps(id);}

}

const api = new ApiClient();
export default api;
